import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonDropdown,
  Card,
  CardBody,
  Col,
  CustomInput,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";
import TemplateWarningCard from "../../../components/common/TemplateWarningCard";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { NotificationManager } from "../../../components/common/react-notifications";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE,
  DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_SELECT_STYLE,
  DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE,
} from "../../../components/common/dashboardUx";
import ExpandableDataCardList from "../../../components/common/ExpandableDataCardList";
import {
  AddCommaNumber,
  getImageUrl,
  getPlaceholderIcon,
} from "../../../helpers/Utils";
import { Pagination } from "../general";
import ModalAddProducts from "./ModalAddProducts";
import ModalAddProductsFromImage from "./ModalAddProductsFromImage";
import { normalizeVariantOption } from "../../../views/app/pages/product/productVariantHandlers";

const LOW_STOCK_THRESHOLD = 10;
const LOW_STOCK_INCOME_RATIO = 0.2;
const FOOD_SERVICE_BUSINESS_TYPES = new Set(["cafe", "restaurant"]);
const DEFAULT_PAGE_SIZE = 12;
const PRODUCT_TOOLBAR_GHOST_BUTTON_STYLE = DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE;
const PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE =
  DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE;
const PRODUCT_TOOLBAR_DANGER_BUTTON_STYLE =
  DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE;
const PRODUCT_TOOLBAR_CONTROL_HEIGHT_STYLE =
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE;
const PRODUCT_TOOLBAR_SELECT_STYLE = DASHBOARD_TOOLBAR_SELECT_STYLE;
const PRODUCT_SHOW_TABLE_VIEW = false;
const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

const statusOptions = [
  { value: "all", label: "Tất cả trạng thái" },
  { value: "active", label: "Đang kinh doanh" },
  { value: "low", label: "Sắp hết hàng" },
  { value: "out", label: "Hết hàng" },
  { value: "unknown", label: "Chưa có dữ liệu kho" },
  { value: "unmanaged", label: "Không quản lý kho" },
  { value: "hidden", label: "Ngừng kinh doanh" },
];

const stockOptions = [
  { value: "all", label: "Trạng thái kho" },
  { value: "in_stock", label: "Còn hàng" },
  { value: "low", label: "Sắp hết" },
  { value: "out", label: "Hết hàng" },
  { value: "unknown", label: "Chưa có dữ liệu kho" },
  { value: "unmanaged", label: "Không quản lý kho" },
];

const statCards = [
  {
    key: "total",
    label: "Tổng sản phẩm",
    icon: "iconsminds-box-full",
    tone: "blue",
    iconBg: "rgba(13, 110, 253, 0.12)",
  },
  {
    key: "active",
    label: "Đang kinh doanh",
    icon: "iconsminds-shop-4",
    tone: "green",
    iconBg: "rgba(111, 66, 193, 0.12)",
  },
  {
    key: "low_out",
    label: "Sắp hết / Hết hàng",
    icon: "simple-icon-exclamation",
    tone: "orange",
    valueFormatted: true,
    iconBg: "rgba(220, 53, 69, 0.12)",
  },
  {
    key: "hidden",
    label: "Ngừng kinh doanh",
    icon: "simple-icon-lock",
    tone: "red",
    iconBg: "rgba(220, 53, 69, 0.12)",
  },
];

const categoryTones = ["blue", "teal", "green", "orange", "red", "purple"];

const BEST_SELLER_RANGE_OPTIONS = [
  { value: "today", label: "Hôm nay" },
  { value: "7", label: "7 ngày" },
  { value: "30", label: "30 ngày" },
  { value: "90", label: "90 ngày" },
  { value: "all", label: "Tất cả" },
];

const getProductCardGridTemplate = (hasDetailColumn = true) =>
  `40px minmax(180px, 1.45fr) minmax(0, 0.8fr) minmax(0, 0.75fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 0.65fr) minmax(85px, 100px) ${
    hasDetailColumn ? "minmax(86px, 0.65fr) " : ""
  }44px`;


const PRODUCT_CARD_COLUMNS = [
  {
    key: "sku_barcode",
    label: "SKU / Barcode",
    cardValueClass: "text-left",
  },
  {
    key: "category",
    label: "Danh mục",
    cardValueClass: "text-left",
  },
  {
    key: "price",
    label: "Giá bán",
    cardValueClass: "text-center",
  },
  {
    key: "cost",
    label: "Giá vốn",
    cardValueClass: "text-center",
  },
  {
    key: "stock",
    label: "Tồn kho",
    cardValueClass: "text-center",
  },
  {
    key: "status",
    label: "Trạng thái",
    cardValueClass: "text-center expandable-data-card-value-status",
    headerClass: "text-center expandable-data-card-header-status",
    overflow: "visible",
    maxWidth: 100,
  },
];

const PRODUCT_CARD_VISUAL_STYLE = {
  active: {
    borderColor: "#dbeafe",
    borderHoverColor: "#bfdbfe",
    bgColor: "#f8fbff",
    hoverBgColor: "#f2f8ff",
    accentColor: "#2563eb",
    titleColor: "#1f2937",
  },
  low: {
    borderColor: "#fed7aa",
    borderHoverColor: "#fdba74",
    bgColor: "#fffaf3",
    hoverBgColor: "#fff7ed",
    accentColor: "#f97316",
    titleColor: "#9a3412",
  },
  out: {
    borderColor: "#fecdd3",
    borderHoverColor: "#fda4af",
    bgColor: "#fff7f8",
    hoverBgColor: "#fff1f2",
    accentColor: "#e11d48",
    titleColor: "#9f1239",
  },
  hidden: {
    borderColor: "#e5e7eb",
    borderHoverColor: "#d1d5db",
    bgColor: "#f9fafb",
    hoverBgColor: "#f3f4f6",
    accentColor: "#6b7280",
    titleColor: "#4b5563",
  },
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const normalizeBusinessType = (businessType) =>
  normalizeText(businessType).trim();

const isFoodServiceBusiness = (businessType) =>
  FOOD_SERVICE_BUSINESS_TYPES.has(normalizeBusinessType(businessType));

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const rawValue = String(value).trim();
  const normalized = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(rawValue)
    ? rawValue.replace(/\./g, "").replace(",", ".")
    : rawValue.replace(/,/g, "");
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) => `${AddCommaNumber(Math.round(value || 0))} đ`;

const getProductVariants = (product) => {
  const dataValues = product?.dataValues || {};
  return safeArray(
    product?.variant ||
      product?.variants ||
      product?.product_variants ||
      product?.productVariants ||
      product?.ProductVariants ||
      product?.ProductVariant ||
      dataValues.variant ||
      dataValues.variants ||
      dataValues.product_variants ||
      dataValues.productVariants ||
      dataValues.ProductVariants ||
      dataValues.ProductVariant,
  );
};

const hiddenVariantAttributeValues = new Set([
  "no_color",
  "no_size",
  "no_material",
  "no-color",
  "no-size",
  "no-material",
]);

const parseVariantAttributes = (variant) => {
  let attributes = variant?.attributes;
  if (typeof attributes === "string") {
    try {
      attributes = JSON.parse(attributes);
    } catch (err) {
      return {};
    }
  }
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(attributes).flatMap(([key, attribute]) => {
      if (attribute === null || attribute === undefined || Array.isArray(attribute)) return [];
      const value = typeof attribute === "object"
        ? attribute.value ?? attribute.label ?? attribute.name
        : attribute;
      if (value === null || value === undefined || typeof value === "object") return [];
      const label = typeof attribute === "object"
        ? attribute.label ?? attribute.name ?? value
        : value;
      const normalized = normalizeVariantOption({ value, label });
      return normalized ? [[key, normalized]] : [];
    }),
  );
};

const getVariantAttributeText = (attribute) => {
  if (!attribute) return "";
  const rawValue =
    typeof attribute === "object"
      ? attribute.value || attribute.label || attribute.name
      : attribute;
  const normalizedValue = normalizeText(rawValue).replace(/\s+/g, "_");
  if (!rawValue || hiddenVariantAttributeValues.has(normalizedValue)) {
    return "";
  }
  if (typeof attribute === "object") {
    return attribute.label || attribute.value || attribute.name || "";
  }
  return String(attribute);
};

const formatVariantAttribute = (variant) => {
  const attributes = parseVariantAttributes(variant);
  const labels = ["size", "color", "material"]
    .map((key) =>
      getVariantAttributeText(attributes?.[key] || variant?.[key]),
    )
    .filter(Boolean);
  return labels.join(" - ") || variant?.name || "Biến thể";
};

const STANDARD_SIZE_ORDER = new Map(
  ["xxxs", "xxs", "xs", "s", "m", "l", "xl", "xxl", "xxxl"].map(
    (size, index) => [size, index],
  ),
);

const normalizeSizeName = (value) =>
  normalizeText(value)
    .trim()
    .replace(/^(size|kich thuoc)\s*/i, "")
    .replace(/[\s_-]+/g, "");

const compareSizeNames = (firstName, secondName) => {
  const normalizedFirst = normalizeSizeName(firstName);
  const normalizedSecond = normalizeSizeName(secondName);
  const firstStandardOrder = STANDARD_SIZE_ORDER.get(normalizedFirst);
  const secondStandardOrder = STANDARD_SIZE_ORDER.get(normalizedSecond);

  if (
    firstStandardOrder !== undefined &&
    secondStandardOrder !== undefined
  ) {
    return firstStandardOrder - secondStandardOrder;
  }
  if (firstStandardOrder !== undefined) return -1;
  if (secondStandardOrder !== undefined) return 1;

  return String(firstName || "").localeCompare(String(secondName || ""), "vi", {
    numeric: true,
    sensitivity: "base",
  });
};

const getVariantSizeName = (variant) => {
  const attributes = parseVariantAttributes(variant);
  return getVariantAttributeText(attributes?.size || variant?.size);
};

const isVariantPromoEffective = (variant, now = new Date()) => {
  const today = new Date(now);
  const boundary = (value, isEnd) => {
    if (!value) return null;
    // Variant bounds are DATEONLY: both boundary days are inclusive locally.
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const date = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(value);
    if (match && isEnd) date.setHours(23, 59, 59, 999);
    return date.getTime();
  };
  const start = boundary(variant?.date_start, false);
  const end = boundary(variant?.date_end, true);
  return Number.isFinite(today.getTime()) &&
    (start === null || (Number.isFinite(start) && today.getTime() >= start)) &&
    (end === null || (Number.isFinite(end) && today.getTime() <= end));
};

const getVariantEffectivePrice = (variant, now = new Date()) => {
  const promoPrice = safeNumber(variant?.promo_price);
  return promoPrice > 0 && isVariantPromoEffective(variant, now)
    ? promoPrice : safeNumber(variant?.price);
};

const compareVariantsByPriceAndSize = (first, second) => {
  const priceDifference =
    getVariantEffectivePrice(first) - getVariantEffectivePrice(second);
  if (priceDifference !== 0) return priceDifference;

  const sizeDifference = compareSizeNames(
    getVariantSizeName(first),
    getVariantSizeName(second),
  );
  if (sizeDifference !== 0) return sizeDifference;

  const attributeDifference = formatVariantAttribute(first).localeCompare(
    formatVariantAttribute(second),
    "vi",
    { numeric: true, sensitivity: "base" },
  );
  if (attributeDifference !== 0) return attributeDifference;

  return safeNumber(first?.id) - safeNumber(second?.id);
};

const getVariantPriceText = (variant, now = new Date()) => {
  const price = safeNumber(variant?.price);
  const promoPrice = getVariantEffectivePrice(variant, now);
  if (promoPrice > 0 && promoPrice !== price) {
    return `${formatMoney(promoPrice)} (giá gốc ${formatMoney(price)})`;
  }
  return formatMoney(price);
};

const formatVariantDateRange = (variant) => {
  const start = variant?.date_start;
  const end = variant?.date_end;
  if (!start && !end) return "—";
  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("vi-VN");
  };
  return [formatDate(start), formatDate(end)].filter(Boolean).join(" - ") || "—";
};

const formatExportDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("vi-VN");
};

const getVariantAttributeExportValue = (variant, key) => {
  const attributes = parseVariantAttributes(variant);
  return getVariantAttributeText(attributes?.[key] || variant?.[key]);
};

const getCategoryName = (product) =>
  product?.category?.name || product?.category_name || "Khác";

const getBrandName = (product) => {
  const brand = product?.brand_name ?? product?.dataValues?.brand_name ??
    product?.brand ?? product?.dataValues?.brand;
  return typeof brand === "object" ? brand?.name || brand?.label || "" : brand || "";
};

const getProviderNames = (product, makeProducts = [], stocks = []) => {
  const nameOf = (value) => typeof value === "object"
    ? value?.name || value?.label || "" : value || "";
  const linkedStockNames = getMakeProductsForProduct(product, makeProducts).map((recipe) => {
    const stock = recipe?.stock || recipe?.Stock ||
      safeArray(stocks).find((item) => String(item.id) === String(recipe.stock_id));
    return nameOf(stock?.company_provider_item?.company_provider ||
      stock?.company_provider || stock?.supplier || stock?.provider);
  });
  return [...new Set([
    nameOf(product?.supplier || product?.dataValues?.supplier),
    nameOf(product?.provider || product?.dataValues?.provider),
    nameOf(product?.company_provider || product?.dataValues?.company_provider),
    ...linkedStockNames,
  ].filter(Boolean))];
};

const getProviderName = (product, makeProducts, stocks) =>
  getProviderNames(product, makeProducts, stocks).join(", ");

const normalizeProductOptionList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  const trimmedValue = value.trim();
  if (!trimmedValue) return [];

  if (trimmedValue.startsWith("[") || trimmedValue.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmedValue);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return [parsed];
    } catch (err) {
      return [];
    }
  }

  return trimmedValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => ({ name: item, label: item, value: item }));
};

const getProductOptionList = (product, keys) => {
  const options = product?.options || {};
  for (const key of keys) {
    const rows = normalizeProductOptionList(product?.[key] ?? options?.[key]);
    if (rows.length) return rows;
  }
  return [];
};

const getOptionName = (option) =>
  option?.name || option?.label || option?.title || option?.value || "";

const getOptionValue = (option) =>
  option?.value ?? option?.price ?? option?.amount ?? "";

const getOptionPromoValue = (option) =>
  option?.promo_price ?? option?.promoPrice ?? option?.price_sale ?? "";

const getOptionNumericPrice = (value) => {
  const rawValue = String(value || "").trim();
  const valueLooksNumeric =
    typeof value === "number" || /^[\d.,]+$/.test(rawValue);
  return valueLooksNumeric ? safeNumber(value) : 0;
};

// Strip the group label prefix from an option name to avoid duplication
// e.g. group label="Size", option name="Size S" → "S"
// e.g. group label="Vị", option name="Vị Nguyên vị" → "Nguyên vị"
const stripGroupPrefix = (name, groupLabel) => {
  if (!name || !groupLabel) return name || "";
  const escaped = groupLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return name.replace(new RegExp(`^${escaped}\\s+`, "i"), "").trim() || name;
};

const formatSizeOptionName = (option, groupLabel = "Size") => {
  const rawName = String(getOptionName(option) || "").trim();
  if (!rawName) return "—";
  return stripGroupPrefix(rawName, groupLabel) || rawName;
};

const formatOptionText = (option) => {
  const name = getOptionName(option);
  const value = getOptionValue(option);
  const promoValue = getOptionPromoValue(option);
  return [name, value, promoValue ? `KM ${promoValue}` : ""]
    .filter((part) => part !== "")
    .join(" | ");
};

const formatOptionChipMeta = (option) => {
  const name = String(getOptionName(option) || "");
  const value = getOptionValue(option);
  const promoValue = getOptionPromoValue(option);
  const parts = [];
  const numericValue = safeNumber(value);
  const valueLooksNumeric =
    typeof value === "number" || /^[\d.,]+$/.test(String(value || "").trim());

  if (valueLooksNumeric && numericValue > 0) {
    parts.push(`+ ${formatMoney(numericValue)}`);
  } else if (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    String(value) !== name
  ) {
    parts.push(String(value));
  }

  if (promoValue !== undefined && promoValue !== null && promoValue !== "") {
    const promoText = safeNumber(promoValue) > 0 ? formatMoney(promoValue) : promoValue;
    parts.push(`KM ${promoText}`);
  }

  return parts.join(" · ");
};

const getOptionPercentNumber = (option) => {
  const percentSource =
    typeof option === "string" || typeof option === "number"
      ? option
      : [getOptionValue(option), getOptionName(option)].find(
          (value) => value !== undefined && value !== null && value !== "",
        );
  const match = String(percentSource ?? "").match(/-?\d+(?:[.,]\d+)?/);
  return match ? safeNumber(match[0]) : null;
};

const formatPercentNumber = (value) => {
  if (!Number.isFinite(value)) return "";
  if (Number.isInteger(value)) return AddCommaNumber(value);
  return String(value).replace(".", ",");
};

const formatPercentOptionValue = (option) => {
  const percentNumber = getOptionPercentNumber(option);
  return percentNumber === null ? "" : `${formatPercentNumber(percentNumber)}%`;
};

const comparePercentOptions = (first, second) => {
  const firstPercent = getOptionPercentNumber(first);
  const secondPercent = getOptionPercentNumber(second);
  if (firstPercent !== null && secondPercent !== null) {
    return firstPercent - secondPercent;
  }
  if (firstPercent !== null) return -1;
  if (secondPercent !== null) return 1;
  return String(getOptionName(first)).localeCompare(String(getOptionName(second)));
};

const getOptionEffectivePrice = (option) => {
  const promoPrice = getOptionNumericPrice(getOptionPromoValue(option));
  return promoPrice > 0
    ? promoPrice
    : getOptionNumericPrice(getOptionValue(option));
};

const compareOptionsByPrice = (first, second) => {
  const priceDifference =
    getOptionEffectivePrice(first) - getOptionEffectivePrice(second);
  if (priceDifference !== 0) return priceDifference;

  return String(getOptionName(first)).localeCompare(
    String(getOptionName(second)),
    "vi",
    { numeric: true, sensitivity: "base" },
  );
};

const compareSizeOptionsByPrice = (first, second) => {
  const priceDifference =
    getOptionEffectivePrice(first) - getOptionEffectivePrice(second);
  if (priceDifference !== 0) return priceDifference;

  return compareSizeNames(getOptionName(first), getOptionName(second));
};

const OptionPriceDisplay = ({ price, promoPrice }) => {
  const priceValue = getOptionNumericPrice(price);
  const promoPriceValue = getOptionNumericPrice(promoPrice);

  if (promoPriceValue > 0) {
    return (
      <span className="product-modern-option-price-row">
        {priceValue > 0 ? (
          <span className="product-modern-option-original-price">
            {formatMoney(priceValue)}
          </span>
        ) : null}
        <span className="product-modern-option-promo-price">
          {formatMoney(promoPriceValue)}
        </span>
      </span>
    );
  }

  if (priceValue > 0) {
    return (
      <span className="product-modern-option-price">
        {formatMoney(priceValue)}
      </span>
    );
  }

  return null;
};

const SizeOptionChip = ({ option, groupLabel = "Size" }) => {
  const price = getOptionValue(option);
  const promoPrice = getOptionPromoValue(option);
  const hasPrice =
    getOptionNumericPrice(price) > 0 || getOptionNumericPrice(promoPrice) > 0;

  return (
    <span className="product-modern-option-chip product-modern-size-option-chip">
      <span className="product-modern-size-option-main">
        <span>{formatSizeOptionName(option, groupLabel)}</span>
        {hasPrice ? (
          <>
            <span className="product-modern-size-option-separator">-</span>
            <OptionPriceDisplay price={price} promoPrice={promoPrice} />
          </>
        ) : null}
      </span>
    </span>
  );
};

const ToppingOptionChip = ({ option, groupLabel = "" }) => {
  const name = stripGroupPrefix(getOptionName(option) || "Topping", groupLabel);
  const extraPrice = getOptionNumericPrice(getOptionValue(option));
  const promoPrice = getOptionNumericPrice(getOptionPromoValue(option));

  return (
    <span className="product-modern-option-chip product-modern-topping-option-chip">
      <span className="product-modern-topping-option-name">{name}</span>
      <span className="product-modern-size-option-separator">-</span>
      <span className="product-modern-option-price">
        Giá thêm: {formatMoney(extraPrice)}
      </span>
      {promoPrice > 0 ? (
        <>
          <span className="product-modern-size-option-separator">-</span>
          <span className="product-modern-option-promo-price">
            Giá KM: {formatMoney(promoPrice)}
          </span>
        </>
      ) : null}
    </span>
  );
};

const getComboOptionItems = (option) => {
  let items = option?.value;
  if (typeof items === "string" && items.trim().startsWith("[")) {
    try { items = JSON.parse(items); } catch (err) { items = []; }
  }
  if (!Array.isArray(items)) items = [option];
  return items.flatMap((item) => {
    const productId = Number(getFirstInventoryValue(item?.product_id,
      item?.combo_product_id, item?.value));
    if (!Number.isFinite(productId) || productId <= 0) return [];
    const quantity = safeNumber(getFirstInventoryValue(item?.quantity,
      item?.count, item?.promo_price, 1));
    return [{ product_id: productId,
      product_name: item?.product_name || item?.name || item?.label || `#${productId}`,
      quantity: quantity > 0 ? quantity : 1 }];
  });
};

const getComboProductText = (option) => {
  const parsedItems = (() => {
    if (!option?.value) return [];
    if (Array.isArray(option.value)) return option.value;
    try {
      const parsed = JSON.parse(option.value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  })();

  if (!parsedItems.length) {
    const name = option?.product_name || getOptionName(option);
    const quantity = option?.quantity ?? option?.count ?? option?.promo_price;
    return [name, quantity ? `SL ${quantity}` : ""].filter(Boolean).join(" - ");
  }
  return parsedItems
    .map((item) => {
      const name = item.product_name || item.name || item.label || item.value || "";
      const quantity = item.quantity || item.count || "";
      return [name, quantity ? `SL ${quantity}` : ""].filter(Boolean).join(" - ");
    })
    .join(", ");
};

const getProductSettingSummary = (product, businessType) => {
  const isFoodService = isFoodServiceBusiness(businessType);
  const sizeOptions = getProductOptionList(product, ["size", "sizes"]);
  const flavorOptions = getProductOptionList(product, ["flavor", "flavors"]);
  const toppingOptions = getProductOptionList(product, ["topping", "toppings"]);
  const iceOptions = getProductOptionList(product, ["ice"]);
  const sugarOptions = getProductOptionList(product, ["sugar"]);
  const colorOptions = getProductOptionList(product, ["color", "colors"]);
  const materialOptions = getProductOptionList(product, ["material", "materials"]);
  const comboOptions = getProductOptionList(product, [
    "combo_product",
    "combo_products",
  ]);

  return {
    size: sizeOptions.map(formatOptionText).join(", "),
    flavor: isFoodService ? flavorOptions.map(formatOptionText).join(", ") : "",
    topping: isFoodService ? toppingOptions.map(formatOptionText).join(", ") : "",
    ice: isFoodService ? iceOptions.map(formatOptionText).join(", ") : "",
    sugar: isFoodService ? sugarOptions.map(formatOptionText).join(", ") : "",
    color: !isFoodService ? colorOptions.map(formatOptionText).join(", ") : "",
    material: !isFoodService ? materialOptions.map(formatOptionText).join(", ") : "",
    combo: comboOptions.map(getComboProductText).join(", "),
  };
};

const getFoodServiceOptionGroups = (product, businessType) => {
  if (!isFoodServiceBusiness(businessType)) return [];
  return [
    {
      key: "size",
      label: product?.sizeTitle || "Size",
      rows: getProductOptionList(product, ["size", "sizes"]),
    },
    {
      key: "flavor",
      label: "Vị",
      rows: getProductOptionList(product, ["flavor", "flavors"]),
    },
    {
      key: "topping",
      label: product?.toppingTitle || "Topping",
      rows: getProductOptionList(product, ["topping", "toppings"]),
    },
    {
      key: "ice",
      label: product?.iceTitle || "Đá",
      rows: getProductOptionList(product, ["ice"]),
    },
    {
      key: "sugar",
      label: product?.sugarTitle || "Đường",
      rows: getProductOptionList(product, ["sugar"]),
    },
    {
      key: "combo",
      label: "Combo",
      rows: getProductOptionList(product, ["combo_product", "combo_products"]),
      formatter: getComboProductText,
    },
  ].filter((group) => group.rows.length > 0);
};

const getRecipeStock = (recipe) => recipe?.stock || recipe?.Stock || {};

const getRecipeName = (recipe) => {
  const stock = getRecipeStock(recipe);
  return stock?.name || recipe?.stock_name || recipe?.name || "Nguyên liệu";
};

const parseRecipeDecimalValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  if (typeof rawValue === "number") {
    return Number.isFinite(rawValue) ? rawValue : null;
  }
  const rawText = String(rawValue).trim();
  if (!rawText) return null;

  const compactText = rawText.replace(/\s/g, "");
  const decimalText = /^-?\d+([.,]\d+)?$/.test(compactText)
    ? compactText.replace(",", ".")
    : "";
  const groupedNumber = /^-?\d{1,3}([.,]\d{3})+([.,]\d+)?$/.test(compactText);
  const value = decimalText ? Number(decimalText) : groupedNumber ? safeNumber(compactText) : NaN;
  return Number.isFinite(value) ? value : null;
};

const getRecipeQuantityValue = (recipe) =>
  parseRecipeDecimalValue(recipe?.count ?? recipe?.quantity);

const formatRecipeQuantityValue = (value) =>
  Number(value).toLocaleString("vi-VN", {
    maximumFractionDigits: 3,
  });

const getRecipeQuantityText = (recipe) => {
  const stock = getRecipeStock(recipe);
  const quantity = getRecipeQuantityValue(recipe);
  const unit = stock?.unit || recipe?.unit || "";
  if (quantity === null) return "—";
  return [formatRecipeQuantityValue(quantity), unit].filter(Boolean).join(" ");
};

const getRecipeUnitCost = (recipe) => {
  const stock = getRecipeStock(recipe);
  return safeNumber(
    getFirstInventoryValue(stock?.avarage_price, stock?.average_price,
      stock?.price_new, recipe?.unit_cost, recipe?.stock_cost_price),
  );
};

const getRecipeStockCountText = (recipe) => {
  const stock = getRecipeStock(recipe);
  const count = parseRecipeDecimalValue(
    stock?.count ?? stock?.income ?? recipe?.stock_quantity,
  );
  if (count === null) return "—";
  return [formatRecipeQuantityValue(count), stock?.unit || recipe?.unit || ""]
    .filter(Boolean)
    .join(" ");
};

const isRecipeStockInactive = (recipe, dataStocks) => {
  const stock = getRecipeStock(recipe);
  if (stock?.is_active === false || stock?.is_active === "false") {
    return true;
  }
  if (Array.isArray(dataStocks) && (recipe?.stock_id || stock?.id)) {
    const targetId = recipe?.stock_id || stock?.id;
    const matchedStock = dataStocks.find((s) => s.id === targetId);
    if (
      matchedStock &&
      (matchedStock.is_active === false || matchedStock.is_active === "false")
    ) {
      return true;
    }
  }
  return false;
};

const getProductDetailInfo = (
  product,
  businessType,
  makeProducts,
  dataStocks,
) => {
  const variants = getProductVariants(product);
  const optionGroups = getFoodServiceOptionGroups(product, businessType);
  const recipes = isFoodServiceBusiness(businessType)
    ? getMakeProductsForProduct(product, makeProducts)
    : [];
  const optionCount = optionGroups.reduce(
    (total, group) => total + group.rows.length,
    0,
  );
  const foodServiceDetailCount = optionCount + recipes.length;
  const detailCount = foodServiceDetailCount + variants.length;
  const hasFoodServiceDetails = foodServiceDetailCount > 0;
  const hasInactiveRecipeStock = recipes.some((recipe) =>
    isRecipeStockInactive(recipe, dataStocks),
  );

  return {
    variants,
    optionGroups,
    recipes,
    detailCount,
    hasDetails: detailCount > 0,
    hasFoodServiceDetails,
    hasInactiveRecipeStock,
    buttonLabel: hasFoodServiceDetails ? "Chi tiết" : "Biến thể",
  };
};

const getProductExportRows = (items, businessType) =>
  items.map((item, index) => {
    const product = item.product;
    const settingSummary = getProductSettingSummary(product, businessType);
    const variants = getProductVariants(product);
    return {
      stt: index + 1,
      product_id: product.id,
      name: product.name,
      sku: product.sku,
      barcode: product.barcode,
      category: item.categoryName,
      provider: item.providerName,
      brand_name: getBrandName(product),
      supplier: item.providerName,
      stock_supplier: item.providerNames?.length === 1 ? item.providerNames[0] : "",
      workbook_version: 2,
      business_type: businessType || "",
      fnb_type: product.fnb_type || "",
      sale_unit: product.sale_unit || "",
      weight: product.weight ?? "",
      length: product.length ?? "",
      width: product.width ?? "",
      height: product.height ?? "",
      dynamic_form: JSON.stringify(parseImportDynamicForm(product.dynamic_form)),
      price: safeNumber(product.price),
      price_sale: safeNumber(product.price_sale),
      active_sale: parseImportBoolean(product.active_sale),
      price_wholesale: safeNumber(product.price_wholesale),
      price_display: item.priceText,
      price_cost: Math.round(item.costValue || 0),
      stock:
        item.inventory.count === null || item.inventory.count === undefined
          ? ""
          : item.inventory.count,
      stock_unit: item.inventory.unit || "",
      stock_source: item.inventory.source || "",
      status: item.status.label,
      sold_count: item.soldCount || 0,
      variant_count: variants.length,
      has_variants: variants.length ? "Có" : "Không",
      size: settingSummary.size,
      flavor: settingSummary.flavor,
      topping: settingSummary.topping,
      ice: settingSummary.ice,
      sugar: settingSummary.sugar,
      color: settingSummary.color,
      material: settingSummary.material,
      combo_products: settingSummary.combo,
      combo_items: JSON.stringify(getProductOptionList(product,
        ["combo_product", "combo_products", "combo_items"]).flatMap(getComboOptionItems)),
      bestter: product.bestter ? "Có" : "Không",
      softHide: product.softHide ? "Có" : "Không",
      tax_rate: safeNumber(product.tax_rate),
      image: getImageValue(product),
      images: JSON.stringify(parseImportImages(product.images || product.image)),
      created_at: formatExportDate(product.createdAt),
      updated_at: formatExportDate(product.updatedAt),
    };
  });

const getVariantExportRows = (items) =>
  items.flatMap((item) => {
    const product = item.product;
    return getProductVariants(product).map((variant, index) => ({
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      variant_index: index + 1,
      variant_id: variant?.id || "",
      variant_name: formatVariantAttribute(variant),
      sku: variant?.sku || "",
      size: getVariantAttributeExportValue(variant, "size"),
      color: getVariantAttributeExportValue(variant, "color"),
      material: getVariantAttributeExportValue(variant, "material"),
      attributes: JSON.stringify(parseVariantAttributes(variant)),
      price: safeNumber(variant?.price),
      price_wholesale: safeNumber(variant?.price_wholesale),
      promo_price: safeNumber(variant?.promo_price),
      cost_price: safeNumber(variant?.cost_price),
      quantity: safeNumber(variant?.quantity),
      exchange_value: safeNumber(variant?.exchange_value || 1),
      date_start: normalizeImportDate(variant?.date_start),
      date_end: normalizeImportDate(variant?.date_end),
      date_range: formatVariantDateRange(variant),
    }));
  });

const getRecipeExportRows = (items, makeProducts) =>
  items.flatMap((item) => {
    const product = item.product;
    return getMakeProductsForProduct(product, makeProducts).map((recipe, index) => {
      const stock = recipe?.stock || {};
      const providerItem = stock?.company_provider_item || {};
      const provider = providerItem?.company_provider || {};
      const quantity = getRecipeQuantityValue(recipe) || 0;
      const unitCost = getRecipeUnitCost(recipe);
      return {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        recipe_index: index + 1,
        stock_id: stock?.id || recipe?.stock_id || "",
        stock_name: stock?.name || "",
        stock_sku: stock?.sku || "",
        barcode: stock?.barcode || "",
        quantity,
        unit: stock?.unit || "",
        unit_cost: unitCost,
        total_cost: quantity * unitCost,
        stock_count: parseRecipeDecimalValue(stock?.count) || 0,
        stock_income: parseRecipeDecimalValue(stock?.income) || 0,
        stock_total: parseRecipeDecimalValue(stock?.total) || 0,
        category: stock?.category_name || "",
        warehouse: stock?.warehouse_name || "",
        supplier: provider?.name || providerItem?.name || "",
        stock_note: stock?.stock_note || "",
      };
    });
  });

const getOptionsExportRows = (items, businessType) =>
  items.flatMap((item) => getProductOptionExportRows(item, businessType));

const applySheetWidths = (worksheet, rows) => {
  if (!rows.length) return worksheet;
  const headers = Object.keys(rows[0]);
  worksheet["!cols"] = headers.map((header) => {
    const maxLength = rows.reduce((max, row) => {
      const valueLength = String(row?.[header] ?? "").length;
      return Math.max(max, valueLength);
    }, header.length);
    return { wch: Math.min(Math.max(maxLength + 2, 10), 42) };
  });
  return worksheet;
};

const appendWorkbookSheet = (workbook, sheetName, rows) => {
  const worksheet = rows.length
    ? XLSX.utils.json_to_sheet(rows)
    : XLSX.utils.aoa_to_sheet([["Không có dữ liệu"]]);
  XLSX.utils.book_append_sheet(
    workbook,
    applySheetWidths(worksheet, rows),
    sheetName,
  );
};

const getImportSheetRows = (workbook, expectedName) => {
  const sheetName = workbook.SheetNames.find(
    (name) => normalizeText(name).trim() === normalizeText(expectedName).trim(),
  );
  if (!sheetName) return [];
  return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
};

const hasModernImportSheets = (workbook) => {
  const sheetNames = workbook.SheetNames.map((name) => normalizeText(name).trim());
  return ["san pham", "bien the", "dinh luong", "tuy chon"].some((name) =>
    sheetNames.includes(name),
  );
};

const getImportRowValue = (row, keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  const normalizedKeys = keys.map((key) => normalizeText(key));
  const foundKey = Object.keys(row).find((rowKey) =>
    normalizedKeys.includes(normalizeText(rowKey)),
  );
  return foundKey ? row[foundKey] : "";
};

const parseImportBoolean = (value) => {
  if (value === true) return true;
  return ["true", "1", "co", "yes", "y", "x"].includes(normalizeText(value));
};

const parseImportDynamicForm = (value) => {
  let parsed = value;
  if (typeof parsed === "string") {
    try { parsed = JSON.parse(parsed); } catch (err) { return {}; }
  }
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};

const parseImportImages = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch (err) {
    // Older spreadsheets used comma-separated URLs.
  }
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseImportSimpleList = (value) => {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => ({ label: item, value: item }));
};

const parseImportPriceList = (value) => {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => {
      const parts = item.trim().split("|");
      const name = parts[0] ? parts[0].trim() : "";
      if (!name) return null;
      const price = safeNumber(parts[1]);
      return { name, label: name, value: parts[1] !== undefined ? price : name, price };
    })
    .filter(Boolean);
};

const parseImportDelimitedRows = (value) => {
  if (!value || typeof value !== "string") return [];
  return value
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => item.split("|").map((part) => part.trim()));
};

const parseImportVariantList = (value) =>
  parseImportDelimitedRows(value).map((parts) => ({
    sku: parts[0] || "",
    attributes: parseVariantAttributes({ attributes: {
      size: parts[1] || "",
      color: parts[2] || "",
      material: parts[3] || "",
    } }),
    price: safeNumber(parts[4]),
    promo_price: safeNumber(parts[5]),
    cost_price: safeNumber(parts[6]),
    quantity: safeNumber(parts[7]),
    exchange_value: safeNumber(parts[8]) || 1,
    price_wholesale: safeNumber(parts[9]),
    date_start: normalizeImportDate(parts[10]),
    date_end: normalizeImportDate(parts[11]),
  }));

const parseImportIngredientList = (value) =>
  parseImportDelimitedRows(value).map((parts) => ({
    stock_id: parts[0] || "",
    stock_name: parts[1] || parts[0] || "",
    name: parts[1] || parts[0] || "",
    quantity: safeNumber(parts[2]) || 1,
    unit: parts[3] || "",
    stock_cost_price: safeNumber(parts[4]),
    stock_quantity: safeNumber(parts[5]),
    stock_supplier: parts[6] || "",
  }));

const buildImportMatchKey = ({ productId, sku, name }) => {
  if (productId !== undefined && productId !== null && productId !== "") {
    return `id:${productId}`;
  }
  if (sku) return `sku:${normalizeText(sku)}`;
  return `name:${normalizeText(name)}`;
};

const resolveImportCategoryId = (categoryName, categories) => {
  if (!categoryName) return null;
  const found = safeArray(categories).find(
    (category) =>
      normalizeText(category.label || category.value || category.name) ===
      normalizeText(categoryName),
  );
  return found ? found.id || found.value : null;
};

const normalizeImportDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    return date ? `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}` : "";
  }
  const localDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value));
  if (localDate) return `${localDate[3]}-${localDate[2].padStart(2, "0")}-${localDate[1].padStart(2, "0")}`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const normalizeImportVariantRow = (row) => ({
  sku: getImportRowValue(row, ["sku", "variant_sku", "SKU"]),
  price: safeNumber(getImportRowValue(row, ["price", "Giá"])),
  price_wholesale: safeNumber(getImportRowValue(row, ["price_wholesale", "Giá sỉ"])),
  promo_price: safeNumber(
    getImportRowValue(row, ["promo_price", "price_sale", "Giá khuyến mãi"]),
  ),
  cost_price: safeNumber(getImportRowValue(row, ["cost_price", "Giá vốn"])),
  quantity: safeNumber(getImportRowValue(row, ["quantity", "Tồn kho", "stock"])),
  exchange_value: safeNumber(getImportRowValue(row, ["exchange_value"])) || 1,
  date_start: normalizeImportDate(getImportRowValue(row, ["date_start"])),
  date_end: normalizeImportDate(getImportRowValue(row, ["date_end"])),
  attributes: parseVariantAttributes({ attributes: getImportRowValue(row, ["attributes"]) || {
    size: getImportRowValue(row, ["size", "Size", "Quy cách", "Dung tích"]),
    color: getImportRowValue(row, ["color", "Màu", "Màu sắc"]),
    material: getImportRowValue(row, ["material", "Chất liệu", "Hương vị"]),
  } }),
});

const normalizeImportRecipeRow = (row) => ({
  stock_id: getImportRowValue(row, ["stock_id", "Mã nguyên liệu"]),
  stock_name: getImportRowValue(row, ["stock_name", "name", "Nguyên liệu"]),
  name: getImportRowValue(row, ["stock_name", "name", "Nguyên liệu"]),
  quantity: safeNumber(getImportRowValue(row, ["quantity", "Số lượng"])) || 1,
  unit: getImportRowValue(row, ["unit", "Đơn vị"]),
  stock_quantity: safeNumber(
    getImportRowValue(row, ["stock_count", "stock_income", "Số lượng nhập"]),
  ),
  stock_cost_price: safeNumber(
    getImportRowValue(row, ["unit_cost", "Giá vốn", "Giá mua"]),
  ),
  stock_supplier: getImportRowValue(row, ["supplier", "Nhà cung cấp"]),
});

const normalizeImportOptionRow = (row) => ({
  alias: normalizeText(getImportRowValue(row, ["option_alias", "alias", "Loại"])),
  option: {
    name: getImportRowValue(row, ["option_name", "name", "Tên tùy chọn"]),
    label: getImportRowValue(row, ["option_name", "name", "Tên tùy chọn"]),
    value: getImportRowValue(row, ["option_value", "value", "Giá trị"]),
    promo_price: getImportRowValue(row, [
      "promo_value",
      "promo_price",
      "Giá khuyến mãi",
    ]),
    product_id: getImportRowValue(row, ["combo_product_id"]),
    quantity: getImportRowValue(row, ["combo_quantity", "quantity"]),
  },
});

const buildLegacyWorkbookImportData = ({ rows, businessType, categories }) => {
  const isFoodService = isFoodServiceBusiness(businessType);
  return rows.map((item, index) => {
    const categoryName = item.category || item["Danh mục"];
    const images = parseImportImages(item.images || item.image || item["Hình ảnh"] || "");
    return {
      id: `import_${index}`,
      name: item.name || item["Tên sản phẩm"],
      barcode: item.barcode || item["Barcode"],
      sku: item.sku || item["SKU"],
      brand_name: item.brand_name || item["Thương hiệu"] || "",
      fnb_type: getImportRowValue(item, ["fnb_type"]) || undefined,
      sale_unit: getImportRowValue(item, ["sale_unit"]),
      weight: getImportRowValue(item, ["weight"]),
      length: getImportRowValue(item, ["length"]),
      width: getImportRowValue(item, ["width"]),
      height: getImportRowValue(item, ["height"]),
      dynamic_form: parseImportDynamicForm(getImportRowValue(item, ["dynamic_form"])),
      category_id: resolveImportCategoryId(categoryName, categories),
      category_name: categoryName,
      price: getImportRowValue(item, ["price", "Giá bán"]),
      price_sale: getImportRowValue(item, ["price_sale", "Giá khuyến mãi"]),
      price_wholesale: item.price_wholesale ?? item["Giá sỉ"],
      active_sale: parseImportBoolean(item.active_sale),
      tax_rate:
        safeNumber(
          item.tax_rate ||
            item["Thuế"] ||
            item["Thuế suất"] ||
            item["Thuế (%)"] ||
            item["Thuế suất (%)"],
        ) || 0,
      image: images[0] || "",
      images,
      bestter: parseImportBoolean(item.bestter),
      softHide: parseImportBoolean(getImportRowValue(item, ["softHide"])),
      sizes: isFoodService
        ? parseImportPriceList(
            item.size || item["Size"] || item["Kích thước"] || item["Quy cách"],
          )
        : parseImportSimpleList(
            item.size ||
              item["Size"] ||
              item["Kích thước"] ||
              item["Đơn vị tính"] ||
              item["Quy cách"] ||
              item["Dung tích"],
          ),
      toppings: isFoodService
        ? parseImportPriceList(item.topping || item["Topping"])
        : [],
      flavors: isFoodService ? parseImportPriceList(item.flavor || item["Hương vị"]) : [],
      ice: isFoodService ? parseImportSimpleList(item.ice) : [],
      sugar: isFoodService ? parseImportSimpleList(item.sugar) : [],
      color: isFoodService
        ? []
        : parseImportSimpleList(
            item.color ||
              item["Màu"] ||
              item["Color"] ||
              item["Màu sắc"] ||
              item["Dung tích / Trọng lượng"],
          ),
      material: isFoodService
        ? []
        : parseImportSimpleList(
            item.material ||
              item["Chất liệu"] ||
              item["Material"] ||
              item["Hương vị"],
          ),
      unit: item.unit || item["Đơn vị"] || (isFoodService ? "" : "cái"),
      stock_quantity: getImportRowValue(item, ["stock_quantity", "stock", "Tồn kho"]),
      stock_cost_price: getImportRowValue(item, ["stock_cost_price", "price_cost", "Giá vốn"]),
      stock_supplier: item.stock_supplier || item.supplier || item.provider || item["Nhà cung cấp"],
      variants: parseImportVariantList(item.variants || item["Biến thể"]),
      combo_items: normalizeProductOptionList(item.combo_items).flatMap(getComboOptionItems),
      ingredients: parseImportIngredientList(
        item.ingredients || item["Định lượng"] || item["Nguyên liệu"],
      ),
    };
  });
};

const buildModernWorkbookImportData = ({ workbook, businessType, categories }) => {
  const productRows = getImportSheetRows(workbook, "San pham");
  const variantRows = getImportSheetRows(workbook, "Bien the");
  const recipeRows = getImportSheetRows(workbook, "Dinh luong");
  const optionRows = getImportSheetRows(workbook, "Tuy chon");
  const isFoodService = isFoodServiceBusiness(businessType);
  const productsByKey = new Map();
  const unmatchedRows = [];

  productRows.forEach((row, index) => {
    const name = getImportRowValue(row, ["name", "Tên sản phẩm"]);
    const sku = getImportRowValue(row, ["sku", "SKU"]);
    const productId = getImportRowValue(row, ["product_id", "id"]);
    const categoryName = getImportRowValue(row, ["category", "Danh mục"]);
    const images = parseImportImages(getImportRowValue(row, ["images", "image"]));
    const key = buildImportMatchKey({ productId, sku, name });
    productsByKey.set(key, {
      id: `import_${index}`,
      import_product_id: productId,
      name,
      sku,
      brand_name: getImportRowValue(row, ["brand_name", "Thương hiệu"]),
      fnb_type: getImportRowValue(row, ["fnb_type"]) || undefined,
      sale_unit: getImportRowValue(row, ["sale_unit"]),
      weight: getImportRowValue(row, ["weight"]),
      length: getImportRowValue(row, ["length"]),
      width: getImportRowValue(row, ["width"]),
      height: getImportRowValue(row, ["height"]),
      dynamic_form: parseImportDynamicForm(getImportRowValue(row, ["dynamic_form"])),
      barcode: getImportRowValue(row, ["barcode", "Barcode"]),
      category_id: resolveImportCategoryId(categoryName, categories),
      category_name: categoryName,
      price: getImportRowValue(row, ["price", "Giá bán"]),
      price_sale: getImportRowValue(row, ["price_sale", "Giá khuyến mãi"]),
      price_wholesale: getImportRowValue(row, ["price_wholesale", "Giá sỉ"]),
      active_sale: Object.keys(row).some((column) => normalizeText(column) === "active_sale")
        ? parseImportBoolean(getImportRowValue(row, ["active_sale"]))
        : safeNumber(getImportRowValue(row, ["price_sale"])) > 0,
      tax_rate:
        safeNumber(
          getImportRowValue(row, [
            "tax_rate",
            "Thuế",
            "Thuế suất",
            "Thuế (%)",
            "Thuế suất (%)",
          ]),
        ) || 0,
      image: images[0] || "",
      images,
      bestter: parseImportBoolean(getImportRowValue(row, ["bestter"])),
      softHide: parseImportBoolean(getImportRowValue(row, ["softHide"])),
      sizes: isFoodService ? parseImportPriceList(getImportRowValue(row, ["size"]))
        : parseImportSimpleList(getImportRowValue(row, ["size"])),
      flavors: isFoodService ? parseImportPriceList(getImportRowValue(row, ["flavor"])) : [],
      toppings: isFoodService
        ? parseImportPriceList(getImportRowValue(row, ["topping"]))
        : [],
      ice: isFoodService
        ? parseImportSimpleList(getImportRowValue(row, ["ice"]))
        : [],
      sugar: isFoodService
        ? parseImportSimpleList(getImportRowValue(row, ["sugar"]))
        : [],
      color: !isFoodService
        ? parseImportSimpleList(getImportRowValue(row, ["color"]))
        : [],
      material: !isFoodService
        ? parseImportSimpleList(getImportRowValue(row, ["material"]))
        : [],
      unit: getImportRowValue(row, ["stock_unit", "unit"]) || "cái",
      stock_quantity: getImportRowValue(row, ["stock", "stock_count"]),
      stock_cost_price: getImportRowValue(row, ["price_cost", "stock_cost_price"]),
      stock_supplier: getImportRowValue(row, ["stock_supplier", "supplier", "provider"]),
      variants: [],
      ingredients: [],
      combo_items: normalizeProductOptionList(getImportRowValue(row, ["combo_items"]))
        .flatMap(getComboOptionItems),
      _importOptionRows: [],
    });
  });

  const attachRow = (row, sheetName, mapper, fieldName) => {
    const productId = getImportRowValue(row, ["product_id", "id"]);
    const sku = getImportRowValue(row, ["product_sku", "sku"]);
    const name = getImportRowValue(row, ["product_name", "name"]);
    const key = buildImportMatchKey({ productId, sku, name });
    const product = productsByKey.get(key);
    if (!product) {
      unmatchedRows.push({
        sheet: sheetName,
        product_id: productId,
        sku,
        name,
        reason: "Không tìm thấy sản phẩm tương ứng trong sheet San pham",
      });
      return;
    }
    product[fieldName].push(mapper(row));
  };

  variantRows.forEach((row) =>
    attachRow(row, "Bien the", normalizeImportVariantRow, "variants"),
  );
  recipeRows.forEach((row) =>
    attachRow(row, "Dinh luong", normalizeImportRecipeRow, "ingredients"),
  );
  optionRows.forEach((row) =>
    attachRow(row, "Tuy chon", normalizeImportOptionRow, "_importOptionRows"),
  );

  productsByKey.forEach((product) => {
    const optionWrappers = product._importOptionRows;
    delete product._importOptionRows;
    const optionFields = { size: "sizes", flavor: "flavors", topping: "toppings",
      ice: "ice", sugar: "sugar", color: "color", material: "material", combo: "combo_items" };
    // Detailed rows are authoritative per group; summaries remain a fallback.
    new Set(optionWrappers.map(({ alias }) => alias)).forEach((alias) => {
      if (optionFields[alias]) product[optionFields[alias]] = [];
    });
    optionWrappers.forEach(({ alias, option }) => {
      const optionValue = {
        ...option,
        name: option.name || option.value,
        label: option.label || option.name || option.value,
      };
      if (alias === "combo") {
        product.combo_items.push(...getComboOptionItems(option));
      } else if (optionFields[alias]) product[optionFields[alias]].push(optionValue);
    });

    if (!isFoodService && product.variants.length) {
      product.stock_quantity = "";
      product.stock_cost_price = "";
    }
  });

  const data = Array.from(productsByKey.values());
  if (data.length && unmatchedRows.length) {
    data[0].__unmatchedImportRows = unmatchedRows;
  }
  return data;
};

const getImportTemplateCategoryName = (categories) => {
  const firstCategory = safeArray(categories).find(
    (category) => category?.label || category?.name || category?.value,
  );
  return (
    firstCategory?.label ||
    firstCategory?.name ||
    firstCategory?.value ||
    "Danh mục mẫu"
  );
};

const getImportTemplateRows = ({ businessType, categories }) => {
  const category = getImportTemplateCategoryName(categories);
  const isFoodService = isFoodServiceBusiness(businessType);
  const normalizedBusinessType = normalizeText(businessType);
  const baseProduct = {
    stt: 1,
    product_id: "SP001",
    name: isFoodService ? "Cà phê sữa đá" : "Sản phẩm mẫu",
    sku: "SKU-SP001",
    barcode: "893000000001",
    category,
    provider: "Nhà cung cấp mẫu",
    business_type: businessType || "",
    price: 45000,
    price_sale: "",
    price_cost: isFoodService ? "" : 25000,
    stock: isFoodService ? "" : 100,
    stock_unit: isFoodService ? "" : "cái",
    size: isFoodService ? "M|5000, L|10000" : "",
    topping: isFoodService ? "Trân châu|8000" : "",
    ice: isFoodService ? "Ít đá, Bình thường" : "",
    sugar: isFoodService ? "30%, 50%, 100%" : "",
    color: "",
    material: "",
    combo_products: "",
    bestter: "Không",
    softHide: "Không",
    image: "",
  };

  const productRows = [baseProduct];
  const variantRows = [];
  const recipeRows = [];
  const optionRows = [];

  if (isFoodService) {
    recipeRows.push({
      product_id: "SP001",
      product_name: baseProduct.name,
      product_sku: baseProduct.sku,
      recipe_index: 1,
      stock_id: "",
      stock_name: "Sữa đặc",
      stock_sku: "NL-SUA",
      barcode: "",
      quantity: 0.03,
      unit: "kg",
      unit_cost: 25000,
      stock_count: 10,
      supplier: "Nhà cung cấp mẫu",
    });
    optionRows.push(
      {
        product_id: "SP001",
        product_name: baseProduct.name,
        sku: baseProduct.sku,
        option_type: "Size",
        option_alias: "size",
        option_name: "M",
        option_value: 5000,
        promo_value: "",
        detail: "M - 5.000đ",
      },
      {
        product_id: "SP001",
        product_name: baseProduct.name,
        sku: baseProduct.sku,
        option_type: "Topping",
        option_alias: "topping",
        option_name: "Trân châu",
        option_value: 8000,
        promo_value: "",
        detail: "Trân châu - 8.000đ",
      },
      {
        product_id: "SP001",
        product_name: baseProduct.name,
        sku: baseProduct.sku,
        option_type: "Đá",
        option_alias: "ice",
        option_name: "Ít đá",
        option_value: "Ít đá",
        promo_value: "",
        detail: "Ít đá",
      },
      {
        product_id: "SP001",
        product_name: baseProduct.name,
        sku: baseProduct.sku,
        option_type: "Đường",
        option_alias: "sugar",
        option_name: "50%",
        option_value: "50%",
        promo_value: "",
        detail: "50%",
      },
    );
  } else if (normalizedBusinessType === "fashion") {
    productRows[0] = {
      ...baseProduct,
      name: "Áo thun mẫu",
      stock: "",
      stock_unit: "cái",
      price_cost: "",
      size: "S, M",
      color: "Trắng, Đen",
      material: "Cotton",
    };
    variantRows.push(
      {
        product_id: "SP001",
        product_name: productRows[0].name,
        product_sku: productRows[0].sku,
        variant_index: 1,
        variant_name: "Size S - Màu trắng - Cotton",
        sku: "SKU-SP001-S-WHITE",
        size: "S",
        color: "Trắng",
        material: "Cotton",
        price: 450000,
        promo_price: 399000,
        cost_price: 220000,
        quantity: 20,
        exchange_value: 1,
      },
      {
        product_id: "SP001",
        product_name: productRows[0].name,
        product_sku: productRows[0].sku,
        variant_index: 2,
        variant_name: "Size M - Màu đen - Cotton",
        sku: "SKU-SP001-M-BLACK",
        size: "M",
        color: "Đen",
        material: "Cotton",
        price: 450000,
        promo_price: "",
        cost_price: 220000,
        quantity: 15,
        exchange_value: 1,
      },
    );
  } else if (
    normalizedBusinessType === "retail" ||
    normalizedBusinessType === "cosmetics" ||
    normalizedBusinessType === "beauty" ||
    normalizedBusinessType === "grocery" ||
    normalizedBusinessType === "pharmacy" ||
    normalizedBusinessType === "electronics" ||
    normalizedBusinessType === "spa" ||
    normalizedBusinessType === "salon"
  ) {
    const isBeauty = normalizedBusinessType === "cosmetics" || normalizedBusinessType === "beauty" || normalizedBusinessType === "spa" || normalizedBusinessType === "salon";
    const isPharmacy = normalizedBusinessType === "pharmacy";
    const isElectronics = normalizedBusinessType === "electronics";
    const isGrocery = normalizedBusinessType === "grocery";

    let sampleName = "Sản phẩm bán lẻ mẫu";
    let sampleSize = "500g, 1kg";
    let sampleColor = "";
    let sampleMaterial = "Vị vani";
    let sampleVarName = "Quy cách 500g - Vị vani";
    let sampleVarSize = "500g";
    let sampleVarColor = "";
    let sampleVarMaterial = "Vị vani";

    if (isBeauty) {
      sampleName = "Kem dưỡng ẩm mẫu";
      sampleSize = "50ml, 100ml";
      sampleColor = "Tự nhiên";
      sampleMaterial = "";
      sampleVarName = "Dung tích 50ml";
      sampleVarSize = "50ml";
      sampleVarColor = "Tự nhiên";
      sampleVarMaterial = "";
    } else if (isPharmacy) {
      sampleName = "Thuốc Paracetamol 500mg mẫu";
      sampleSize = "Hộp 10 vỉ, Hộp 20 vỉ";
      sampleColor = "500mg";
      sampleMaterial = "Paracetamol";
      sampleVarName = "Hộp 10 vỉ - Hàm lượng 500mg";
      sampleVarSize = "Hộp 10 vỉ";
      sampleVarColor = "500mg";
      sampleVarMaterial = "Paracetamol";
    } else if (isElectronics) {
      sampleName = "Điện thoại thông minh mẫu";
      sampleSize = "128GB, 256GB";
      sampleColor = "Đen, Trắng";
      sampleMaterial = "Kim loại";
      sampleVarName = "Dung lượng 128GB - Màu Đen";
      sampleVarSize = "128GB";
      sampleVarColor = "Đen";
      sampleVarMaterial = "Kim loại";
    } else if (isGrocery) {
      sampleName = "Sữa tươi mẫu";
      sampleSize = "Thùng 48 hộp, Vỉ 4 hộp";
      sampleColor = "Có đường";
      sampleMaterial = "Sữa";
      sampleVarName = "Vỉ 4 hộp - Có đường";
      sampleVarSize = "Vỉ 4 hộp";
      sampleVarColor = "Có đường";
      sampleVarMaterial = "Sữa";
    }

    productRows[0] = {
      ...baseProduct,
      name: sampleName,
      size: sampleSize,
      color: sampleColor,
      material: sampleMaterial,
    };
    variantRows.push({
      product_id: "SP001",
      product_name: productRows[0].name,
      product_sku: productRows[0].sku,
      variant_index: 1,
      variant_name: sampleVarName,
      sku: "SKU-SP001-V1",
      size: sampleVarSize,
      color: sampleVarColor,
      material: sampleVarMaterial,
      price: 120000,
      promo_price: "",
      cost_price: 70000,
      quantity: 50,
      exchange_value: 1,
    });
  } else {
    productRows[0] = {
      ...baseProduct,
      size: "Mẫu 1",
      color: "Màu mẫu",
      material: "Chất liệu mẫu",
    };
  }

  return {
    guideRows: [
      {
        sheet: "San pham",
        note: "Bắt buộc nhập name và category. product_id dùng để liên kết các sheet phụ; có thể dùng mã tạm như SP001.",
      },
      {
        sheet: "Bien the",
        note: "Dùng cho sản phẩm có biến thể. product_id/product_sku/product_name phải khớp sheet San pham.",
      },
      {
        sheet: "Dinh luong",
        note: "Dùng cho cafe/restaurant hoặc sản phẩm có định lượng nguyên liệu.",
      },
      {
        sheet: "Tuy chon",
        note: "option_alias hợp lệ: size, topping, ice, sugar, color, material, combo.",
      },
    ],
    productRows,
    variantRows,
    recipeRows,
    optionRows,
  };
};

const buildImportTemplateWorkbook = ({ businessType, categories }) => {
  const workbook = XLSX.utils.book_new();
  const { productRows, variantRows, recipeRows } = getImportTemplateRows({
    businessType,
    categories,
  });
  const templateRows = productRows.map((row) => {
    const rowVariants = variantRows.filter(
      (variant) => String(variant.product_id) === String(row.product_id),
    );
    const rowRecipes = recipeRows.filter(
      (recipe) => String(recipe.product_id) === String(row.product_id),
    );
    return {
      ...row,
      variants: rowVariants
        .map((variant) =>
          [
            variant.sku,
            variant.size,
            variant.color,
            variant.material,
            variant.price,
            variant.promo_price,
            variant.cost_price,
            variant.quantity,
            variant.exchange_value,
          ].join("|"),
        )
        .join("; "),
      ingredients: rowRecipes
        .map((recipe) =>
          [
            recipe.stock_id,
            recipe.stock_name,
            recipe.quantity,
            recipe.unit,
            recipe.unit_cost,
            recipe.stock_count,
            recipe.supplier,
          ].join("|"),
        )
        .join("; "),
      note:
        "variants: sku|size|color|material|price|promo_price|cost_price|quantity|exchange_value; ingredients: stock_id|stock_name|quantity|unit|unit_cost|stock_count|supplier",
    };
  });
  const headers = [
    "stt",
    "product_id",
    "name",
    "sku",
    "barcode",
    "category",
    "provider",
    "business_type",
    "price",
    "price_sale",
    "price_cost",
    "stock",
    "stock_unit",
    "size",
    "topping",
    "ice",
    "sugar",
    "color",
    "material",
    "combo_products",
    "bestter",
    "softHide",
    "image",
    "variants",
    "ingredients",
    "note",
  ];
  const worksheet = XLSX.utils.json_to_sheet(templateRows, {
    header: headers,
    skipHeader: false,
  });
  XLSX.utils.book_append_sheet(
    workbook,
    applySheetWidths(worksheet, templateRows),
    "Template",
  );

  return workbook;
};

const getProductOptionExportRows = (item, businessType) => {
  const product = item.product;
  const isFoodService = isFoodServiceBusiness(businessType);
  const optionGroups = [
    {
      type: product?.sizeTitle || "Size",
      alias: "size",
      rows: getProductOptionList(product, ["size", "sizes"]),
    },
    {
      type: product?.flavorTitle || "Hương vị",
      alias: "flavor",
      rows: isFoodService ? getProductOptionList(product, ["flavor", "flavors"]) : [],
    },
    {
      type: product?.toppingTitle || "Topping",
      alias: "topping",
      rows: isFoodService
        ? getProductOptionList(product, ["topping", "toppings"])
        : [],
    },
    {
      type: product?.iceTitle || "Đá",
      alias: "ice",
      rows: isFoodService ? getProductOptionList(product, ["ice"]) : [],
    },
    {
      type: product?.sugarTitle || "Đường",
      alias: "sugar",
      rows: isFoodService ? getProductOptionList(product, ["sugar"]) : [],
    },
    {
      type: "Màu sắc",
      alias: "color",
      rows: !isFoodService
        ? getProductOptionList(product, ["color", "colors"])
        : [],
    },
    {
      type: "Chất liệu",
      alias: "material",
      rows: !isFoodService
        ? getProductOptionList(product, ["material", "materials"])
        : [],
    },
    {
      type: "Combo",
      alias: "combo",
      rows: getProductOptionList(product, ["combo_product", "combo_products", "combo_items"]),
    },
  ];

  return optionGroups.flatMap((group) =>
    group.rows.flatMap((option) => (group.alias === "combo"
      ? getComboOptionItems(option) : [option]).map((entry) => ({
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      option_type: group.type,
      option_alias: group.alias,
      option_name: group.alias === "combo" ? entry.product_name : getOptionName(entry),
      option_value: group.alias === "combo" ? String(entry.product_id) : getOptionValue(entry),
      promo_value: group.alias === "combo" ? entry.quantity : getOptionPromoValue(entry),
      combo_product_id: group.alias === "combo" ? entry.product_id : "",
      combo_quantity: group.alias === "combo" ? entry.quantity : "",
      detail:
        group.alias === "combo" ? getComboProductText(entry) : formatOptionText(entry),
    }))),
  );
};

const getSoldCount = (product) => {
  const direct = [
    product?.sold_count,
    product?.soldCount,
    product?.total_sold,
    product?.totalSold,
    product?.sold,
  ].find((value) => value !== undefined && value !== null && value !== "");
  if (direct !== undefined) return safeNumber(direct);

  const orders = safeArray(product?.order || product?.orders);
  if (!orders.length) return 0;
  return orders.reduce(
    (total, order) =>
      total + safeNumber(order?.quantity || order?.count || order?.qty || 1),
    0,
  );
};

const getSoldCountByRange = (product, range) => {
  const ranges = product?.sold_count_ranges || product?.soldCountRanges;
  if (ranges && ranges[range] !== undefined && ranges[range] !== null) {
    return safeNumber(ranges[range]);
  }
  return range === "all" ? getSoldCount(product) : 0;
};

const getImageValue = (product) => {
  const images = product?.images;
  if (images) {
    try {
      const parsed = typeof images === "string" ? JSON.parse(images) : images;
      if (Array.isArray(parsed) && parsed.length) return parsed[0];
    } catch (err) {
      const firstImage = String(images)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)[0];
      if (firstImage) return firstImage;
    }
  }
  return product?.image || "";
};

const getProductImageUrl = (product) => getImageUrl(getImageValue(product));

const getMakeProductsForProduct = (product, makeProducts) =>
  safeArray(makeProducts).filter(
    (makeProduct) => String(makeProduct.product_id) === String(product.id),
  );

const hasInventoryValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const getFirstInventoryValue = (...values) =>
  values.find((value) => hasInventoryValue(value));

const getRecipeAvailableStockCount = (recipe) => {
  const stock = getRecipeStock(recipe);
  const count = getFirstInventoryValue(
    stock?.count,
    recipe?.stock_quantity,
    recipe?.stock_count,
    stock?.income,
  );
  return hasInventoryValue(count) ? parseRecipeDecimalValue(count) : null;
};

const getFoodServiceProducibleInventory = (product, makeProducts) => {
  const recipeRows = getMakeProductsForProduct(product, makeProducts)
    .map((recipe) => {
      const usage = getRecipeQuantityValue(recipe);
      return usage > 0 ? { recipe, usage } : null;
    })
    .filter(Boolean);

  if (!recipeRows.length) return null;

  const recipeAvailability = recipeRows.map(({ recipe, usage }) => {
    const availableStock = getRecipeAvailableStockCount(recipe);
    return {
      recipe,
      usage,
      availableStock,
      producibleCount: availableStock === null ? null : Math.max(0, Math.floor(availableStock / usage)),
    };
  });

  return {
    count: recipeAvailability.some((item) => item.producibleCount === null)
      ? null : Math.min(...recipeAvailability.map((item) => item.producibleCount)),
    unit: "SP",
    source: "recipe",
    recipeAvailability,
  };
};

const getInventoryInfo = (product, makeProducts, businessType) => {
  const variants = getProductVariants(product);
  if (variants.length) {
    const quantities = variants.map((variant) => parseRecipeDecimalValue(variant?.quantity));
    return {
      count: quantities.some((quantity) => quantity === null) ? null
        : quantities.reduce((total, quantity) => total + quantity, 0),
      min_stock_threshold: product?.min_stock_threshold,
      unit: "Cái",
      source: "variant",
    };
  }

  if (isFoodServiceBusiness(businessType)) {
    const recipeInventory = getFoodServiceProducibleInventory(
      product,
      makeProducts,
    );
    if (recipeInventory) return recipeInventory;
  }

  const productMakeProducts = getMakeProductsForProduct(product, makeProducts);
  const stockRecipe = productMakeProducts.find(
    (recipe) => getRecipeQuantityValue(recipe) === 1 && recipe?.stock,
  );
  if (stockRecipe?.stock) {
    return {
      count: parseRecipeDecimalValue(stockRecipe.stock.count),
      income: parseRecipeDecimalValue(stockRecipe.stock.income) || 0,
      min_stock_threshold: stockRecipe.stock.min_stock_threshold,
      unit: stockRecipe.stock.unit || "",
      source: "stock",
    };
  }

  if (
    product?.quantity !== undefined ||
    product?.stock_quantity !== undefined ||
    product?.quantitative !== undefined ||
    product?.count !== undefined
  ) {
    return {
      count: parseRecipeDecimalValue(
        getFirstInventoryValue(
          product.quantity,
          product.stock_quantity,
          product.quantitative,
          product.count,
        ),
      ),
      income: parseRecipeDecimalValue(
        getFirstInventoryValue(product.income, product.total),
      ),
      unit: product.unit || "Cái",
      min_stock_threshold: product.min_stock_threshold,
      source: "product",
    };
  }

  if (isFoodServiceBusiness(businessType)) {
    return { count: null, unit: "", source: "service" };
  }

  return { count: null, unit: "", source: "unknown" };
};

const getLowStockThreshold = (inventory, businessType) => {
  const configuredThreshold = parseRecipeDecimalValue(inventory?.min_stock_threshold);
  if (configuredThreshold !== null && configuredThreshold >= 0) return configuredThreshold;
  if (
    !isFoodServiceBusiness(businessType) &&
    parseRecipeDecimalValue(inventory?.income) > 0
  ) {
    return parseRecipeDecimalValue(inventory.income) * LOW_STOCK_INCOME_RATIO;
  }

  return LOW_STOCK_THRESHOLD;
};

const getCostInfo = (product, makeProducts) => {
  const variants = getProductVariants(product);
  const variantCosts = variants
    .filter((variant) => hasInventoryValue(variant.cost_price))
    .map((variant) => safeNumber(variant.cost_price))
    .filter((cost) => cost >= 0);
  if (variantCosts.length) {
    const minCost = Math.min(...variantCosts);
    const maxCost = Math.max(...variantCosts);
    const costedVariants = variants.filter((variant) =>
      hasInventoryValue(variant.cost_price) && safeNumber(variant.cost_price) >= 0);
    const quantity = costedVariants.reduce((total, variant) =>
      total + Math.max(0, parseRecipeDecimalValue(variant.quantity) ?? 0), 0);
    const value = Math.round(quantity > 0
      ? costedVariants.reduce((total, variant) => total + safeNumber(variant.cost_price) *
        Math.max(0, parseRecipeDecimalValue(variant.quantity) ?? 0), 0) / quantity
      : variantCosts.reduce((total, cost) => total + cost, 0) / variantCosts.length);
    const text =
      minCost === maxCost
        ? formatMoney(minCost)
        : `${formatMoney(minCost)} - ${formatMoney(maxCost)}`;
    return {
      hasRange: minCost !== maxCost,
      maxCost,
      minCost,
      text,
      value,
    };
  }

  const recipeRows = getMakeProductsForProduct(product, makeProducts);
  const recipeCost = recipeRows.reduce((total, recipe) =>
    total + getRecipeUnitCost(recipe) * (getRecipeQuantityValue(recipe) ?? 1), 0);
  if (recipeRows.some((recipe) => {
    const stock = getRecipeStock(recipe);
    return [stock.avarage_price, stock.average_price, stock.price_new,
      recipe.unit_cost, recipe.stock_cost_price].some(hasInventoryValue);
  })) {
    return {
      hasRange: false,
      maxCost: recipeCost,
      minCost: recipeCost,
      text: formatMoney(recipeCost),
      value: recipeCost,
    };
  }

  const value = safeNumber(getFirstInventoryValue(product?.price_cost, product?.stock_cost_price));
  return {
    hasRange: false,
    maxCost: value,
    minCost: value,
    text: formatMoney(value),
    value,
  };
};

const buildPriceRangeInfo = (prices) => {
  if (!prices.length) return null;

  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const text =
    minPrice === maxPrice
      ? formatMoney(minPrice)
      : `${formatMoney(minPrice)} - ${formatMoney(maxPrice)}`;

  return {
    hasRange: minPrice !== maxPrice,
    hasSale: false,
    maxPrice,
    minPrice,
    price: minPrice,
    salePrice: 0,
    text,
  };
};

const getPriceInfo = (product, businessType, now = new Date()) => {
  if (isFoodServiceBusiness(businessType)) {
    const sizePrices = getProductOptionList(product, ["size", "sizes"])
      .map((size) => {
        const promoPrice = getOptionNumericPrice(getOptionPromoValue(size));
        return promoPrice > 0
          ? promoPrice
          : getOptionNumericPrice(getOptionValue(size));
      })
      .filter((price) => price > 0);
    const sizePriceInfo = buildPriceRangeInfo(sizePrices);
    if (sizePriceInfo) return sizePriceInfo;
  }

  const variants = getProductVariants(product);
  if (variants.length) {
    const prices = variants
      .map((variant) => getVariantEffectivePrice(variant, now))
      .filter((price) => price >= 0);
    const variantPriceInfo = buildPriceRangeInfo(prices);
    if (variantPriceInfo) return variantPriceInfo;
  }

  const salePrice = safeNumber(product?.price_sale);
  const price = safeNumber(product?.price);
  const hasSale = parseImportBoolean(product?.active_sale) && salePrice > 0 && salePrice !== price;
  return {
    hasSale,
    price,
    salePrice,
    text: hasSale
      ? `${formatMoney(price)} → ${formatMoney(salePrice)}`
      : formatMoney(price),
  };
};

const getPriceDisplay = (product, businessType) =>
  getPriceInfo(product, businessType).text;

const getStatusMeta = (product, inventory, businessType) => {
  if (product?.softHide) {
    return {
      key: "hidden",
      label: "Ngừng kinh doanh",
      className: "product-modern-status-hidden",
    };
  }

  if (inventory.count === null || inventory.count === undefined) {
    const isService = inventory.source === "service";
    return {
      key: isService ? "unmanaged" : "unknown",
      label: isService ? "Không quản lý kho" : "Chưa có dữ liệu kho",
      className: "product-modern-status-hidden",
    };
  }

  if (inventory.count <= 0) {
    return {
      key: "out",
      label: "Hết hàng",
      className: "product-modern-status-out",
    };
  }

  const lowStockThreshold = getLowStockThreshold(inventory, businessType);
  const configuredRecipeStocks = safeArray(inventory.recipeAvailability).filter((item) => {
    const threshold = parseRecipeDecimalValue(getRecipeStock(item.recipe).min_stock_threshold);
    return threshold !== null && threshold >= 0;
  });
  const isLowStock = configuredRecipeStocks.length
    ? configuredRecipeStocks.some((item) => item.availableStock <=
      parseRecipeDecimalValue(getRecipeStock(item.recipe).min_stock_threshold))
    : inventory.count <= lowStockThreshold;

  if (isLowStock) {
    return {
      key: "low",
      label: "Sắp hết hàng",
      className: "product-modern-status-low",
    };
  }

  return {
    key: "active",
    label: "Tồn kho ổn",
    className: "product-modern-status-active",
  };
};

const enrichProducts = (products, makeProducts, businessType, stocks) =>
  safeArray(products).map((product) => {
    const inventory = getInventoryInfo(product, makeProducts, businessType);
    const status = getStatusMeta(product, inventory, businessType);
    const costInfo = getCostInfo(product, makeProducts);
    return {
      product,
      inventory,
      status,
      categoryName: getCategoryName(product),
      brandName: getBrandName(product),
      providerNames: getProviderNames(product, makeProducts, stocks),
      providerName: getProviderName(product, makeProducts, stocks),
      soldCount: getSoldCount(product),
      costInfo,
      costValue: costInfo.value,
      priceInfo: getPriceInfo(product, businessType),
      priceText: getPriceDisplay(product, businessType),
    };
  });

const getActiveFilterCount = (filters) =>
  Object.keys(filters).reduce(
    (total, key) => total + (filters[key] && filters[key] !== "all" ? 1 : 0),
    0,
  );

const matchesProductStatusFilter = (item, statusFilter) => {
  if (!statusFilter || statusFilter === "all") return true;
  if (statusFilter === "active") return !item.product?.softHide;
  if (statusFilter === "hidden") return Boolean(item.product?.softHide);
  return item.status.key === statusFilter;
};

const getInventoryText = (item) =>
  item.inventory.count === null || item.inventory.count === undefined
    ? "—"
    : `${formatRecipeQuantityValue(item.inventory.count)} ${
        item.inventory.unit || ""
      }`;

const ProductPriceDisplay = ({ priceInfo }) => {
  if (priceInfo?.hasRange) {
    const rangeText = `${formatMoney(priceInfo.minPrice)} - ${formatMoney(priceInfo.maxPrice)}`;
    return (
      <div
        className="product-modern-price-range"
        title={rangeText}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        <span>{formatMoney(priceInfo.minPrice)}</span>
        <span>{formatMoney(priceInfo.maxPrice)}</span>
      </div>
    );
  }

  if (priceInfo?.hasSale) {
    const saleTitle = `Giá gốc: ${formatMoney(priceInfo.price)} | Giá KM: ${formatMoney(priceInfo.salePrice)}`;
    return (
      <div
        className="product-modern-price-stack"
        title={saleTitle}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        <span className="product-modern-original-price">
          {formatMoney(priceInfo.price)}
        </span>
        <span className="product-modern-sale-price">
          {formatMoney(priceInfo.salePrice)}
        </span>
      </div>
    );
  }

  return (
    <div
      className="product-modern-money"
      title={priceInfo?.text || formatMoney(0)}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%",
      }}
    >
      {priceInfo?.text || formatMoney(0)}
    </div>
  );
};

const ProductCostDisplay = ({ costInfo, costValue }) => {
  if (costInfo?.hasRange) {
    const rangeText = `${formatMoney(costInfo.minCost)} - ${formatMoney(costInfo.maxCost)}`;
    return (
      <div
        className="product-modern-price-range"
        title={rangeText}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        <span>{formatMoney(costInfo.minCost)}</span>
        <span>{formatMoney(costInfo.maxCost)}</span>
      </div>
    );
  }

  const costText = costInfo?.text || formatMoney(costValue || 0);
  return (
    <div
      className="product-modern-money"
      title={costText}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%",
      }}
    >
      {costText}
    </div>
  );
};

const renderProductCardColumnValue = (item, columnKey) => {
  const product = item.product;
  switch (columnKey) {
    case "sku_barcode":
      return (
        <div className="w-100 min-width-zero" style={{ overflow: "hidden", maxWidth: "100%" }}>
          <div
            className="product-modern-primary-text"
            title={product.sku || "—"}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {product.sku || "—"}
          </div>
          <div
            className="product-modern-secondary-text"
            title={product.barcode || "Chưa có barcode"}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {product.barcode || "Chưa có barcode"}
          </div>
        </div>
      );
    case "category":
      return (
        <div className="w-100 min-width-zero" style={{ overflow: "hidden", maxWidth: "100%" }}>
          <div
            className="product-modern-primary-text"
            title={item.categoryName || "—"}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {item.categoryName}
          </div>
          <div
            className="product-modern-secondary-text"
            title={item.providerName || "-"}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {item.providerName || "-"}
          </div>
        </div>
      );
    case "price":
      return <ProductPriceDisplay priceInfo={item.priceInfo} />;
    case "cost":
      return (
        <ProductCostDisplay
          costInfo={item.costInfo}
          costValue={item.costValue}
        />
      );
    case "stock":
      return (
        <span
          className={classnames("product-modern-stock", {
            "product-modern-stock-low": item.status.key === "low",
            "product-modern-stock-out": item.status.key === "out",
          })}
          title={getInventoryText(item)}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            maxWidth: "100%",
          }}
        >
          {getInventoryText(item)}
        </span>
      );
    case "status":
      return <StatusPill status={item.status} />;
    default:
      return (
        <span
          title={product[columnKey] || "—"}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            maxWidth: "100%",
          }}
        >
          {product[columnKey] || "—"}
        </span>
      );
  }
};

const ProductThumb = ({ product, businessType }) => {
  const [hasImageError, setHasImageError] = useState(false);
  const imageUrl = getProductImageUrl(product);

  useEffect(() => {
    setHasImageError(false);
  }, [imageUrl]);

  if (!imageUrl || hasImageError) {
    return (
      <div className="product-modern-thumb product-modern-thumb-placeholder">
        <i className={getPlaceholderIcon(businessType, product?.fnb_type)} />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={product?.name || "product"}
      className="product-modern-thumb"
      onError={() => setHasImageError(true)}
    />
  );
};

const SummaryCard = ({ card, value }) => (
  <Col key={card.key} md="6" lg="3" className="mb-2">
    <Card>
      <CardBody className="py-3 d-flex align-items-center">
        <div
          className="d-flex align-items-center justify-content-center mr-2"
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            backgroundColor: card.iconBg,
          }}
        >
          <i
            className={card.icon}
            style={{ color: card.tone, fontSize: "1.15rem" }}
          />
        </div>
        <div>
          <div className="text-muted text-small mb-2">{card.label}</div>
          <div className="h6 mb-0">{card.valueFormatted ? value : AddCommaNumber(value)}</div>
        </div>
      </CardBody>
    </Card>
  </Col>
  // <div className="product-modern-summary-card">
  //   <div className={`product-modern-summary-icon product-modern-tone-${card.tone}`}>
  //     <i className={card.icon} />
  //   </div>
  //   <div>
  //     <div className="product-modern-summary-label">{card.label}</div>
  //     <div className="product-modern-summary-value">
  //       {card.valueFormatted ? value : AddCommaNumber(value)}
  //     </div>
  //   </div>
  // </div>
);

const StatusPill = ({ status }) => (
  <span
    className={`product-modern-status-pill ${status.className}`}
    style={{
      whiteSpace: "nowrap",
      overflow: "visible",
      textOverflow: "clip",
      maxWidth: "100px",
      fontSize: "11px",
      padding: "3px 6px",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    {status.label}
  </span>
);

const ProductActions = ({
  item,
  canUpdate,
  canDelete,
  onUpdateProduct,
  onUpdateCost,
  onDeleteProduct,
  onSoftHideProduct,
  onBestterProduct,
}) => (
  <div className="d-flex justify-content-center align-items-center" style={{ width: 32, minWidth: 32, flexShrink: 0 }}>
    {/* <Button
      color="light"
      size="sm"
      className="product-modern-icon-button"
      disabled={!canUpdate}
      onClick={() => onUpdateProduct(item.product)}
    >
      <i className="simple-icon-pencil" />
    </Button> */}
    <UncontrolledDropdown className="d-inline-block">
      <DropdownToggle
        // color="light"
        color="link"
        size="sm"
        caret={false}
        // className="product-modern-icon-button"
        className="p-0 text-muted d-inline-flex align-items-center justify-content-center"
        style={{
          textDecoration: "none",
          boxShadow: "none",
          width: 22,
          height: 22,
        }}
      >
        {/* <i className="simple-icon-options-vertical" /> */}
        <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>
          ⋮
        </span>
      </DropdownToggle>
      <DropdownMenu right>
        <DropdownItem disabled={!canUpdate} onClick={() => onUpdateProduct(item.product)}>
          <i className="simple-icon-pencil mr-2" />
          Cập nhật sản phẩm
        </DropdownItem>
        {/* <DropdownItem disabled={!canUpdate} onClick={() => onUpdateCost(item.product)}>
          <i className="simple-icon-layers mr-2" />
          Cập nhật giá vốn
        </DropdownItem> */}
        <DropdownItem divider />
        <DropdownItem disabled={!canUpdate} onClick={() => onBestterProduct(item.product)}>
          <i className="simple-icon-star mr-2" />
          {item.product?.bestter ? "Bỏ bán chạy" : "Đánh dấu bán chạy"}
        </DropdownItem>
        <DropdownItem disabled={!canUpdate} onClick={() => onSoftHideProduct(item.product)}>
          <i className="simple-icon-lock mr-2" />
          {item.product?.softHide ? "Mở kinh doanh" : "Ngừng kinh doanh"}
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          disabled={!canDelete}
          onClick={() => onDeleteProduct(item.product)}
          className="text-danger"
        >
          <i className="simple-icon-trash mr-2" />
          Xóa sản phẩm
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  </div>
);

const VariantExpandButton = ({ expanded, count, label = "Biến thể", onClick }) => (
  <Button
    color="link"
    className="product-modern-variant-toggle"
    onClick={onClick}
    aria-label={expanded ? `Thu gọn ${label}` : `Mở rộng ${label}`}
  >
    <span>
      {label} ({count})
    </span>
    <span className="product-modern-variant-toggle-icon">
      {expanded ? "▴" : "▾"}
    </span>
  </Button>
);

const VariantPanel = ({ product, variants }) => (
  <div className="product-modern-variant-panel">
    <div className="product-modern-variant-header">
      <div>Biến thể</div>
      <div>SKU</div>
      <div>Giá bán</div>
      <div>Tồn kho</div>
      <div>Khuyến mãi</div>
    </div>
    {[...variants]
      .sort(compareVariantsByPriceAndSize)
      .map((variant, index) => (
        <div
          className="product-modern-variant-row"
          key={variant?.id || `${product.id}-${index}`}
        >
          <div className="product-modern-primary-text">
            {formatVariantAttribute(variant)}
          </div>
          <div className="product-modern-secondary-text">
            {variant?.sku || "—"}
          </div>
          <div className="product-modern-money">{getVariantPriceText(variant)}</div>
          <div className="product-modern-stock">
            {AddCommaNumber(safeNumber(variant?.quantity))}
          </div>
          <div className="product-modern-secondary-text">
            {formatVariantDateRange(variant)}
          </div>
        </div>
      ))}
  </div>
);

const ProductDetailSection = ({
  title,
  icon,
  children,
  className,
  businessType,
}) => (
  // <div className={classnames("product-modern-detail-section", className)}>
  <div className={classnames("", className)}>
    {isFoodServiceBusiness(businessType) ? (
      <div className="product-modern-detail-section-title">
        <i className={`${icon} mr-2`} />
        {title}
      </div>
    ) : null}
    {children}
  </div>
);

const ProductOptionsPanel = ({ optionGroups, businessType }) => (
  <ProductDetailSection
    title="Tuỳ chọn bán hàng"
    icon="simple-icon-tag"
    businessType={businessType}
  >
    <div className="product-modern-option-groups">
      {optionGroups.map((group) => {
        const isPercentGroup = ["ice", "sugar"].includes(group.key);
        const isPriceGroup = ["size", "flavor", "topping"].includes(group.key);
        const rows = isPercentGroup
          ? [...group.rows].sort(comparePercentOptions)
          : group.key === "size"
            ? [...group.rows].sort(compareSizeOptionsByPrice)
            : isPriceGroup
            ? [...group.rows].sort(compareOptionsByPrice)
            : group.rows;

        return (
          <div className="product-modern-option-group" key={group.key}>
            <div className="product-modern-option-label">{group.label}</div>
            <div className="product-modern-option-chip-row">
              {rows.map((option, index) => {
                const name = group.formatter
                  ? group.formatter(option)
                  : stripGroupPrefix(getOptionName(option), group.label);
                if (group.key === "size") {
                  return (
                    <SizeOptionChip
                      key={`${group.key}-${index}-${name}`}
                      option={option}
                      groupLabel={group.label}
                    />
                  );
                }

                if (group.key === "topping" || group.key === "flavor") {
                  return (
                    <ToppingOptionChip
                      key={`${group.key}-${index}-${name}`}
                      option={option}
                      groupLabel={group.label}
                    />
                  );
                }

                if (isPercentGroup) {
                  const percentText = formatPercentOptionValue(option);
                  const displayName =
                    name && name !== percentText ? name : percentText || "Tuỳ chọn";
                  const meta = displayName !== percentText ? percentText : "";
                  return (
                    <span
                      className="product-modern-option-chip product-modern-percent-option-chip"
                      key={`${group.key}-${index}-${displayName}`}
                    >
                      <span>{meta || displayName}</span>
                    </span>
                  );
                }

                const meta = group.key === "combo" ? "" : formatOptionChipMeta(option);
                return (
                  <span
                    className="product-modern-option-chip"
                    key={`${group.key}-${index}-${name}`}
                  >
                    <span>{name || "Tuỳ chọn"}</span>
                    {meta ? <small>{meta}</small> : null}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </ProductDetailSection>
);

const RecipePanel = ({ recipes, businessType, dataStocks }) => {
  const hasInactiveStock = recipes.some((recipe) =>
    isRecipeStockInactive(recipe, dataStocks),
  );

  return (
    <ProductDetailSection
      title="Định lượng"
      icon="simple-icon-layers"
      businessType={businessType}
    >
      {hasInactiveStock && (
        <Alert color="warning" className="p-2 mb-2 text-small d-flex align-items-center">
          <i
            className="simple-icon-exclamation mr-2 font-weight-bold"
            style={{ fontSize: "16px" }}
          />
          <div>
            <strong>Lưu ý:</strong> Món này có nguyên liệu định lượng đang <strong>ngừng kinh doanh</strong>. Vui lòng cập nhật nguyên liệu thay thế để đảm bảo tính giá vốn và trừ kho chính xác.
          </div>
        </Alert>
      )}
      <div className="product-modern-recipe-list">
        <div className="product-modern-recipe-header">
          <div>Nguyên liệu</div>
          <div>Số lượng dùng</div>
          <div>Giá vốn</div>
          <div>Tồn kho</div>
        </div>
        {[...recipes]
          .sort((first, second) => {
            const firstCost =
              (getRecipeQuantityValue(first) || 0) * getRecipeUnitCost(first);
            const secondCost =
              (getRecipeQuantityValue(second) || 0) * getRecipeUnitCost(second);
            return secondCost - firstCost;
          })
          .map((recipe, index) => {
            const quantity = getRecipeQuantityValue(recipe) || 0;
            const unitCost = getRecipeUnitCost(recipe);
            const isInactive = isRecipeStockInactive(recipe, dataStocks);
            return (
              <div
                className={classnames("product-modern-recipe-row", {
                  "product-modern-recipe-row-inactive": isInactive,
                })}
                style={
                  isInactive
                    ? { backgroundColor: "#fef2f2", borderRadius: "4px" }
                    : undefined
                }
                key={
                  recipe?.id ||
                  recipe?.stock_id ||
                  `${getRecipeName(recipe)}-${index}`
                }
              >
                <div>
                  <div className="product-modern-primary-text d-flex align-items-center">
                    <span>{getRecipeName(recipe)}</span>
                    {isInactive && (
                      <Badge color="danger" pill className="ml-2" style={{ fontSize: "10px" }}>
                        Ngừng KD
                      </Badge>
                    )}
                  </div>
                  <div className="product-modern-secondary-text">
                    {getRecipeStock(recipe)?.sku || recipe?.stock_sku || "—"}
                  </div>
                </div>
                <div className="product-modern-recipe-cell">
                  <small>Số lượng dùng</small>
                  <span>{getRecipeQuantityText(recipe)}</span>
                </div>
                <div className="product-modern-recipe-cell">
                  <small>Giá vốn</small>
                  <span>{formatMoney(quantity * unitCost)}</span>
                </div>
                <div className="product-modern-recipe-cell">
                  <small>Tồn kho</small>
                  <span>{getRecipeStockCountText(recipe)}</span>
                </div>
              </div>
            );
          })}
      </div>
    </ProductDetailSection>
  );
};

const ProductDetailPanel = ({
  product,
  businessType,
  makeProducts,
  dataStocks,
  statusKey = "active",
}) => {
  const { variants, optionGroups, recipes } = getProductDetailInfo(
    product,
    businessType,
    makeProducts,
    dataStocks,
  );

  return (
    <div
      className={classnames(
        "product-modern-detail-panel",
        `product-modern-detail-panel-tone-${statusKey}`,
      )}
    >
      <div className="product-modern-detail-grid">
        {optionGroups.length ? (
          <ProductOptionsPanel
            optionGroups={optionGroups}
            businessType={businessType}
          />
        ) : null}
        {recipes.length ? (
          <RecipePanel
            recipes={recipes}
            businessType={businessType}
            dataStocks={dataStocks}
          />
        ) : null}
        {variants.length ? (
          <ProductDetailSection
            title="Biến thể"
            icon="simple-icon-grid"
            className="product-modern-detail-section-wide"
            businessType={businessType}
          >
            <VariantPanel product={product} variants={variants} />
          </ProductDetailSection>
        ) : null}
      </div>
    </div>
  );
};

const MOBILE_STATUS_FILTERS = [
  { value: "all", label: "Tất cả" },
  { value: "active", label: "Đang bán" },
  { value: "low", label: "Sắp hết" },
  { value: "out", label: "Hết hàng" },
  { value: "unknown", label: "Chưa có kho" },
  { value: "unmanaged", label: "Không quản lý kho" },
  { value: "hidden", label: "Ngừng bán" },
];

const MobileProductRow = ({
  item,
  businessType,
  selected,
  selectionMode,
  templateLabel,
  canUpdate,
  canDelete,
  onToggleProduct,
  onUpdateProduct,
  onUpdateCost,
  onDeleteProduct,
  onSoftHideProduct,
  onBestterProduct,
  hasInactiveRecipeStock,
}) => {
  const product = item.product;

  return (
    <div
      className={classnames("product-modern-mobile-item", {
        "product-modern-mobile-item-muted": product.softHide,
        "product-modern-mobile-item-selected": selected,
      })}
    >
      <div
        className="product-modern-mobile-item-main"
        role={canUpdate ? "button" : undefined}
        tabIndex={canUpdate ? 0 : undefined}
        onClick={() => canUpdate && onUpdateProduct(product)}
        onKeyDown={(event) => {
          if (canUpdate && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onUpdateProduct(product);
          }
        }}
      >
        {selectionMode ? (
          <Input
            type="checkbox"
            className="product-modern-mobile-checkbox"
            checked={selected}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => onToggleProduct(product.id, event)}
            aria-label={`Chọn ${product.name}`}
          />
        ) : null}

        <ProductThumb product={product} businessType={businessType} />

        <div className="product-modern-mobile-info">
          <div className="product-modern-mobile-name-row">
            {product.bestter ? <span className="product-modern-mobile-hot">HOT</span> : null}
            <strong>{product.name}</strong>
            {product.isTemplate ? (
              <span className="badge badge-info ml-1">{templateLabel}</span>
            ) : null}
            {hasInactiveRecipeStock ? (
              <span
                className="badge badge-warning ml-1 text-extra-small"
                style={{
                  backgroundColor: "#fef3c7",
                  color: "#d97706",
                  border: "1px solid #fde68a",
                }}
                title="Định lượng có nguyên liệu đã ngừng kinh doanh"
              >
                Định lượng ngừng KD
              </span>
            ) : null}
          </div>
          <div className="product-modern-mobile-meta">
            {[product.sku || "Chưa có SKU", item.categoryName, item.providerName]
              .filter(Boolean)
              .join(" · ")}
          </div>
          <div
            className={classnames("product-modern-mobile-stock", {
              "is-low": item.status.key === "low",
              "is-out": item.status.key === "out",
              "is-hidden": item.status.key === "hidden",
            })}
          >
            {item.status.label} · {getInventoryText(item)}
          </div>
        </div>

        <div className="product-modern-mobile-price">
          <ProductPriceDisplay priceInfo={item.priceInfo} />
        </div>

        <div
          className="product-modern-mobile-item-actions"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <ProductActions
            item={item}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onUpdateProduct={onUpdateProduct}
            onUpdateCost={onUpdateCost}
            onDeleteProduct={onDeleteProduct}
            onSoftHideProduct={onSoftHideProduct}
            onBestterProduct={onBestterProduct}
          />
        </div>
      </div>
    </div>
  );
};

const ProductRow = ({
  item,
  businessType,
  selected,
  highlighted,
  canUpdate,
  canDelete,
  onToggleProduct,
  onUpdateProduct,
  onUpdateCost,
  onDeleteProduct,
  onSoftHideProduct,
  onBestterProduct,
  expanded,
  onToggleExpand,
  hasInactiveRecipeStock,
}) => {
  const product = item.product;
  const variants = getProductVariants(product);
  const hasVariants = variants.length > 0;
  const inventoryText = getInventoryText(item);

  return (
    <>
      <tr
        id={`product-card-${product.id}`}
        className={classnames({
          "product-modern-row-muted": product.softHide,
          "table-primary font-weight-bold": highlighted,
        })}
      >
        <td className="product-modern-table-check">
          <CustomInput
            type="checkbox"
            id={`product-modern-check-${product.id}`}
            checked={selected}
            onChange={() => onToggleProduct(product.id)}
            label=""
          />
        </td>
        <td>
          <div className="product-modern-product-cell">
            <ProductThumb product={product} businessType={businessType} />
            <div className="min-width-zero">
              <div className="product-modern-product-name">{product.name}</div>
              {hasInactiveRecipeStock ? (
                <div
                  className="product-modern-product-badge"
                  style={{
                    color: "#d97706",
                    backgroundColor: "#fef3c7",
                    border: "1px solid #fde68a",
                    padding: "1px 6px",
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: "11px",
                    display: "inline-flex",
                    alignItems: "center",
                    marginTop: "2px",
                  }}
                  title="Định lượng có nguyên liệu đã ngừng kinh doanh"
                >
                  <i className="simple-icon-exclamation mr-1" />
                  Định lượng có nguyên liệu ngừng KD
                </div>
              ) : null}
              {product.bestter ? (
                <div className="product-modern-product-badge">
                  <i className="simple-icon-star mr-1" />
                  Bán chạy
                </div>
              ) : null}
            </div>
          </div>
        </td>
        <td>
          <div className="product-modern-primary-text">{product.sku || "—"}</div>
          <div className="product-modern-secondary-text">
            {product.barcode || "Chưa có barcode"}
          </div>
        </td>
        <td>
          <div className="product-modern-primary-text">{item.categoryName}</div>
          <div className="product-modern-secondary-text">
            {item.providerName || "-"}
          </div>
        </td>
        <td>
          <ProductPriceDisplay priceInfo={item.priceInfo} />
        </td>
        <td>
          <ProductCostDisplay
            costInfo={item.costInfo}
            costValue={item.costValue}
          />
        </td>
        <td>
          <span
            className={classnames("product-modern-stock", {
              "product-modern-stock-low": item.status.key === "low",
              "product-modern-stock-out": item.status.key === "out",
            })}
          >
            {inventoryText}
          </span>
        </td>
        <td>
          <StatusPill status={item.status} />
        </td>
        <td>
          <div className="product-modern-table-actions">
            {hasVariants ? (
              <VariantExpandButton
                expanded={expanded}
                count={variants.length}
                onClick={() => onToggleExpand(product.id)}
              />
            ) : null}
            <ProductActions
              item={item}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onUpdateProduct={onUpdateProduct}
              onUpdateCost={onUpdateCost}
              onDeleteProduct={onDeleteProduct}
              onSoftHideProduct={onSoftHideProduct}
              onBestterProduct={onBestterProduct}
            />
          </div>
        </td>
      </tr>
      {expanded && hasVariants ? (
        <tr className="product-modern-variant-table-row">
          <td colSpan="9">
            <VariantPanel product={product} variants={variants} />
          </td>
        </tr>
      ) : null}
    </>
  );
};

// LEGACY_PRODUCT_CARD_COMPONENT_START
// const ProductCard = ({
//   item,
//   businessType,
//   selected,
//   canUpdate,
//   canDelete,
//   onToggleProduct,
//   onUpdateProduct,
//   onUpdateCost,
//   onDeleteProduct,
//   onSoftHideProduct,
//   onBestterProduct,
//   expanded,
//   onToggleExpand,
// }) => {
//   const product = item.product;
//   const variants = getProductVariants(product);
//   const hasVariants = variants.length > 0;
//   const inventoryText =
//     item.inventory.count === null || item.inventory.count === undefined
//       ? "—"
//       : `${AddCommaNumber(item.inventory.count)} ${item.inventory.unit || ""}`;
// 
//   return (
//     <div
//       className={classnames(
//         "product-modern-card-item",
//         `product-modern-card-tone-${item.status.key}`,
//         { "product-modern-row-muted": product.softHide },
//       )}
//     >
//       {/* LEGACY_PRODUCT_TILE_CARD_START
//       <div className="product-modern-card-top">
//         <CustomInput
//           type="checkbox"
//           id={`product-modern-card-check-${product.id}`}
//           checked={selected}
//           onChange={() => onToggleProduct(product.id)}
//           label=""
//         />
//         <StatusPill status={item.status} />
//       </div>
//       <ProductThumb product={product} businessType={businessType} />
//       <div className="product-modern-card-name">{product.name}</div>
//       <div className="product-modern-secondary-text">{product.sku || "—"}</div>
//       <div className="product-modern-card-meta">
//         <span>{item.categoryName}</span>
//         <strong>{item.priceText}</strong>
//       </div>
//       <ProductActions
//         item={item}
//         canUpdate={canUpdate}
//         canDelete={canDelete}
//         onUpdateProduct={onUpdateProduct}
//         onUpdateCost={onUpdateCost}
//         onDeleteProduct={onDeleteProduct}
//         onSoftHideProduct={onSoftHideProduct}
//         onBestterProduct={onBestterProduct}
//       />
//       LEGACY_PRODUCT_TILE_CARD_END */}
//       <div className="product-modern-card-row">
//         <div className="product-modern-card-check">
//           <CustomInput
//             type="checkbox"
//             id={`product-modern-card-check-${product.id}`}
//             checked={selected}
//             onChange={() => onToggleProduct(product.id)}
//             label=""
//           />
//         </div>
//         <div className="product-modern-card-product">
//           <ProductThumb product={product} businessType={businessType} />
//           <div className="min-width-zero">
//             <div className="product-modern-card-name">{product.name}</div>
//             {product.bestter ? (
//               <div className="product-modern-product-badge">
//                 <i className="simple-icon-star mr-1" />
//                 Bán chạy
//               </div>
//             ) : null}
//           </div>
//         </div>
//         <div>
//           <div className="product-modern-primary-text">{product.sku || "—"}</div>
//           <div className="product-modern-secondary-text">
//             {product.barcode || "Chưa có barcode"}
//           </div>
//         </div>
//         <div>
//           <div className="product-modern-primary-text">{item.categoryName}</div>
//           <div className="product-modern-secondary-text">
//             {item.brandName || "Chưa phân thương hiệu"}
//           </div>
//         </div>
//         <div className="product-modern-money">{item.priceText}</div>
//         <div className="product-modern-money">{formatMoney(item.costValue)}</div>
//         <div>
//           <span
//             className={classnames("product-modern-stock", {
//               "product-modern-stock-low": item.status.key === "low",
//               "product-modern-stock-out": item.status.key === "out",
//             })}
//           >
//             {inventoryText}
//           </span>
//         </div>
//         <div>
//           <StatusPill status={item.status} />
//         </div>
//         <div className="product-modern-card-actions">
//           {hasVariants ? (
//             <VariantExpandButton
//               expanded={expanded}
//               count={variants.length}
//               onClick={() => onToggleExpand(product.id)}
//             />
//           ) : null}
//           <ProductActions
//             item={item}
//             canUpdate={canUpdate}
//             canDelete={canDelete}
//             onUpdateProduct={onUpdateProduct}
//             onUpdateCost={onUpdateCost}
//             onDeleteProduct={onDeleteProduct}
//             onSoftHideProduct={onSoftHideProduct}
//             onBestterProduct={onBestterProduct}
//           />
//         </div>
//       </div>
//       {expanded && hasVariants ? (
//         <VariantPanel product={product} variants={variants} />
//       ) : null}
//     </div>
//   );
// };
// LEGACY_PRODUCT_CARD_COMPONENT_END

const ProductModernDashboard = ({
  data_all,
  data_categories,
  data_providers,
  data_stocks,
  make_products,
  business_type,
  showTemplate,
  page,
  selectedItems,
  hide_button_add,
  canCreate,
  canUpdate,
  canDelete,
  onAddProduct,
  onImportProduct,
  onSelectAllProducts,
  onToggleProduct,
  onUpdateProduct,
  onUpdateCost,
  onDeleteProduct,
  onSoftHideProduct,
  onBestterProduct,
  onBulkDelete,
  onBulkSoftHide,
  onBulkBestter,
  onOpenCategories,
  onAddCategory,
  onAddFromTemplate,
  onSearch,
  onRefresh,
  intl,
}) => {
  const pageSearch = page?.search || "";
  const productDataLength = safeArray(data_all).length;
  const [filters, setFilters] = useState({
    category: "all",
    status: "all",
    provider: "all",
    stock: "all",
  });
  const [viewMode, setViewMode] = useState("card");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [bestSellerRange, setBestSellerRange] = useState("30");
  const [importOpen, setImportOpen] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [mobileCreateMenuOpen, setMobileCreateMenuOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSelectionMode, setMobileSelectionMode] = useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(20);
  const [mobileSearch, setMobileSearch] = useState(pageSearch);
  const [expandedCardIds, setExpandedCardIds] = useState([]);
  const [savedImportData, setSavedImportData] = useState(() => {
    const saved = sessionStorage.getItem("modalProductsData");
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch (err) {
      return null;
    }
  });

  const products = useMemo(
    () => enrichProducts(data_all, make_products, business_type, data_stocks),
    [data_all, make_products, business_type, data_stocks],
  );

  const categoryOptions = useMemo(() => {
    const names = new Set(
      safeArray(data_categories)
        .map((category) => category?.name)
        .filter(Boolean),
    );
    products.forEach((item) => names.add(item.categoryName));
    return [
      { value: "all", label: "Tất cả danh mục" },
      ...Array.from(names).map((name) => ({ value: name, label: name })),
    ];
  }, [data_categories, products]);

  const providerOptions = useMemo(() => {
    const names = new Set(
      safeArray(data_providers)
        .map((provider) => provider?.name || provider?.label)
        .filter(Boolean),
    );
    products.forEach((item) => {
      item.providerNames.forEach((name) => names.add(name));
    });
    return [
      { value: "all", label: "Tất cả nhà cung cấp" },
      ...Array.from(names).map((name) => ({ value: name, label: name })),
    ];
  }, [data_providers, products]);

  const filteredProducts = useMemo(() => {
    const searchText = normalizeText(page?.search);
    return products.filter((item) => {
      const product = item.product;
      if (filters.category !== "all" && item.categoryName !== filters.category) {
        return false;
      }
      if (!matchesProductStatusFilter(item, filters.status)) {
        return false;
      }
      if (filters.provider !== "all" && !item.providerNames.includes(filters.provider)) {
        return false;
      }
      if (filters.stock === "in_stock" && !(item.inventory.count > 0)) {
        return false;
      }
      if (filters.stock === "low" && item.status.key !== "low") {
        return false;
      }
      if (filters.stock === "out" && item.status.key !== "out") {
        return false;
      }
      if (["unknown", "unmanaged"].includes(filters.stock) && item.status.key !== filters.stock) {
        return false;
      }
      if (searchText) {
        const haystack = normalizeText(
          `${product.name} ${product.sku} ${product.barcode} ${item.categoryName} ${item.brandName} ${item.providerName}`,
        );
        if (!haystack.includes(searchText)) return false;
      }
      return true;
    });
  }, [filters, page, products]);

  const totalPage = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPage);
  const visibleProducts = filteredProducts.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize,
  );

  const hasAnyProductDetails = useMemo(() => {
    return visibleProducts.some(
      (item) =>
        getProductDetailInfo(item.product, business_type, make_products, data_stocks).hasDetails
    );
  }, [visibleProducts, business_type, make_products, data_stocks]);

  const productCardGridTemplate = useMemo(() => {
    return getProductCardGridTemplate(hasAnyProductDetails);
  }, [hasAnyProductDetails]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, pageSearch, pageSize, productDataLength]);

  useEffect(() => {
    if (currentPage > totalPage) setCurrentPage(totalPage);
  }, [currentPage, totalPage]);

  useEffect(() => {
    setMobileSearch(pageSearch);
  }, [pageSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (mobileSearch !== pageSearch && onSearch) {
        onSearch(mobileSearch);
      }
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [mobileSearch, onSearch, pageSearch]);

  useEffect(() => {
    setMobileVisibleCount(20);
  }, [filters, pageSearch, productDataLength]);

  const handleChangePage = (pageNumber) => {
    const nextPage = Math.min(Math.max(pageNumber, 1), totalPage);
    setCurrentPage(nextPage);
  };

  const stats = useMemo(() => {
    const initialStats = {
      total: products.length,
      active: 0,
      out: 0,
      low: 0,
      hidden: 0,
    };
    products.forEach((item) => {
      if (item.status.key === "hidden") {
        initialStats.hidden += 1;
      } else {
        initialStats.active += 1;
      }

      if (item.status.key === "out") initialStats.out += 1;
      if (item.status.key === "low") initialStats.low += 1;
    });
    return initialStats;
  }, [products]);

  const categoryStats = useMemo(() => {
    const counter = new Map();
    products.forEach((item) => {
      counter.set(item.categoryName, (counter.get(item.categoryName) || 0) + 1);
    });
    return Array.from(counter.entries())
      .sort(([, firstCount], [, secondCount]) => secondCount - firstCount)
      .slice(0, 5)
      .map(([name, count], index) => ({
        name,
        count,
        tone: categoryTones[index % categoryTones.length],
      }));
  }, [products]);

  const bestSellers = useMemo(
    () =>
      [...products]
        .map((item) => ({
          ...item,
          bestSellerSoldCount: getSoldCountByRange(
            item.product,
            bestSellerRange,
          ),
        }))
        .sort((a, b) => {
          if (!!a.product?.bestter !== !!b.product?.bestter) {
            return b.product?.bestter ? 1 : -1;
          }
          if (a.bestSellerSoldCount !== b.bestSellerSoldCount) {
            return b.bestSellerSoldCount - a.bestSellerSoldCount;
          }
          return safeNumber(b.product?.price) - safeNumber(a.product?.price);
        })
        .slice(0, 5),
    [bestSellerRange, products],
  );

  const selectedIds = safeArray(selectedItems);
  const visibleIds = visibleProducts.map((item) => item.product.id);
  const isAllVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) => selectedIds.includes(id));
  const activeFilterCount = getActiveFilterCount(filters);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ category: "all", status: "all", provider: "all", stock: "all" });
  };

  const [highlightedProductId, setHighlightedProductId] = useState(null);

  const handleBestSellerClick = (product) => {
    if (!product || !product.id) return;

    if (String(highlightedProductId) === String(product.id)) {
      setHighlightedProductId(null);
      return;
    }

    setHighlightedProductId(product.id);

    // 1. Kiểm tra xem sản phẩm có trong danh sách lọc hiện tại hay không
    const indexInFiltered = filteredProducts.findIndex(
      (item) => String(item.product?.id || item.id) === String(product.id),
    );

    if (indexInFiltered !== -1) {
      // Đã có trong danh sách lọc: tính trang và nhảy đến trang đó nếu cần
      const targetPage = Math.floor(indexInFiltered / pageSize) + 1;
      if (targetPage !== safeCurrentPage) {
        setCurrentPage(targetPage);
      }
    } else {
      // Bị bộ lọc ẩn đi: Bỏ lọc để sản phẩm xuất hiện
      resetFilters();
      const indexInAll = products.findIndex(
        (item) => String(item.product?.id || item.id) === String(product.id),
      );
      if (indexInAll !== -1) {
        const targetPage = Math.floor(indexInAll / pageSize) + 1;
        setCurrentPage(targetPage);
      }
    }

    // Cuộn mượt đến card sản phẩm
    setTimeout(() => {
      const cardElement = document.getElementById(`product-card-${product.id}`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  };

  const toggleCardExpand = (productId) => {
    setExpandedCardIds((previous) =>
      previous.includes(productId)
        ? previous.filter((item) => item !== productId)
        : [...previous, productId],
    );
  };

  const handleAddProduct = () => {
    if (!canCreate) return;
    if (hide_button_add) {
      NotificationManager.error(
        intl.formatMessage({
          id: "page.heading.error.limit",
          defaultMessage: "Bạn đã đạt giới hạn sản phẩm",
        }),
        intl.formatMessage({
          id: "page.heading.error.limit_title",
          defaultMessage: "Không thể thêm sản phẩm",
        }),
        3000,
      );
      return;
    }
    onAddProduct();
  };

  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const productRows = getProductExportRows(filteredProducts, business_type);
    const variantRows = getVariantExportRows(filteredProducts);
    const recipeRows = getRecipeExportRows(filteredProducts, make_products);
    const optionRows = getOptionsExportRows(filteredProducts, business_type);

    appendWorkbookSheet(workbook, "San pham", productRows);
    appendWorkbookSheet(workbook, "Bien the", variantRows);
    appendWorkbookSheet(workbook, "Dinh luong", recipeRows);
    appendWorkbookSheet(workbook, "Tuy chon", optionRows);

    if (!workbook.SheetNames.length) {
      appendWorkbookSheet(workbook, "San pham", []);
    }

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `Danh sách sản phẩm ${business_type || "tat-ca"}${fileExtension}`,
    );
  };

  const handleImportDataChange = (data) => {
    setSavedImportData(data);
    sessionStorage.setItem("modalProductsData", JSON.stringify(data));
  };

  const handleDownloadImportTemplate = () => {
    const workbook = buildImportTemplateWorkbook({
      businessType: business_type,
      categories: data_categories,
    });
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `Mẫu nhập sản phẩm ${business_type || "tat-ca"}${fileExtension}`,
    );
  };

  const handleImportFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const fileData = new Uint8Array(loadEvent.target.result);
      const workbook = XLSX.read(fileData, { type: "array" });
      const mappedData = hasModernImportSheets(workbook)
        ? buildModernWorkbookImportData({
            workbook,
            businessType: business_type,
            categories: data_categories,
          })
        : buildLegacyWorkbookImportData({
            rows: XLSX.utils.sheet_to_json(
              workbook.Sheets[workbook.SheetNames[0]],
              { defval: "" },
            ),
            businessType: business_type,
            categories: data_categories,
          });
      handleImportDataChange(mappedData);
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const displayStart = filteredProducts.length
    ? (safeCurrentPage - 1) * pageSize + 1
    : 0;
  const displayEnd = Math.min(safeCurrentPage * pageSize, filteredProducts.length);
  const mobileVisibleProducts = filteredProducts.slice(0, mobileVisibleCount);
  const hasTemplateProducts =
    (Array.isArray(data_all) && data_all.some((p) => Boolean(p?.isTemplate))) ||
    (Array.isArray(products) &&
      products.some(
        (item) => Boolean(item?.product?.isTemplate || item?.isTemplate),
      )) ||
    (Array.isArray(filteredProducts) &&
      filteredProducts.some(
        (item) => Boolean(item?.product?.isTemplate || item?.isTemplate),
      ));

  return (
    <>
    <div className="product-modern-mobile d-md-none">
      <div className="product-modern-mobile-header">
        <div>
          <h1>Sản phẩm</h1>
        </div>
        <div>
          {onAddFromTemplate && (
            <Button
              color="outline-secondary"
              size="md"
              className="mr-2"
              onClick={onAddFromTemplate}
            >
              Nhập từ mẫu
            </Button>
          )}

          <ButtonDropdown
            isOpen={mobileCreateMenuOpen}
            toggle={() => setMobileCreateMenuOpen((value) => !value)}
          >
            <Button
              color="primary"
              disabled={!canCreate}
              onClick={handleAddProduct}
              style={{"marginRight": "0.15rem"}}
            >
              <span>Thêm sản phẩm</span>
            </Button>
            <DropdownToggle caret color="primary" />
            <DropdownMenu>
              <DropdownItem onClick={onAddCategory}>
                <i className="simple-icon-plus mr-2" />
                Tạo danh mục
              </DropdownItem>
              <DropdownItem onClick={onOpenCategories}>
                <i className="simple-icon-folder-alt mr-2" />
                Quản lý danh mục
              </DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>
        </div>
      </div>

      {hasTemplateProducts ? (
        <div className="px-3 mb-2">
          <TemplateWarningCard compact onDismissed={onRefresh} />
        </div>
      ) : null}

      <div className="product-modern-mobile-search">
        <i className="simple-icon-magnifier" />
        <input
          type="search"
          value={mobileSearch}
          onChange={(event) => setMobileSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && onSearch) {
              onSearch(event.currentTarget.value);
            }
          }}
          placeholder="Tìm tên, SKU, barcode..."
          aria-label="Tìm kiếm sản phẩm"
        />
        {mobileSearch ? (
          <button
            type="button"
            onClick={() => setMobileSearch("")}
            aria-label="Xóa tìm kiếm"
          >
            <i className="simple-icon-close" />
          </button>
        ) : null}
      </div>

      <div className="product-modern-mobile-chips">
        {MOBILE_STATUS_FILTERS.map((option) => (
          <button
            type="button"
            key={option.value}
            className={classnames({ active: filters.status === option.value })}
            onClick={() => updateFilter("status", option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="product-modern-mobile-stats">
        <button type="button" onClick={() => updateFilter("status", "all")}>
          <strong>{AddCommaNumber(stats.total)}</strong>
          <span>Tổng</span>
        </button>
        <button type="button" onClick={() => updateFilter("status", "active")}>
          <strong className="is-success">{AddCommaNumber(stats.active)}</strong>
          <span>Đang bán</span>
        </button>
        <button type="button" onClick={() => updateFilter("status", "low")}>
          <strong className="is-warning">{AddCommaNumber(stats.low)}</strong>
          <span>Sắp hết</span>
        </button>
        <button type="button" onClick={() => updateFilter("status", "out")}>
          <strong className="is-danger">{AddCommaNumber(stats.out)}</strong>
          <span>Hết hàng</span>
        </button>
      </div>

      <div className="product-modern-mobile-list-toolbar">
        <button
          type="button"
          className={classnames("product-modern-mobile-filter-button", {
            active: activeFilterCount > 0,
          })}
          onClick={() => setMobileFilterOpen(true)}
        >
          <i className="simple-icon-equalizer" />
          Bộ lọc
          {activeFilterCount ? <span>{activeFilterCount}</span> : null}
        </button>
        <button
          type="button"
          className={classnames("product-modern-mobile-select-all", {
            active: mobileSelectionMode,
          })}
          onClick={() => setMobileSelectionMode((value) => !value)}
        >
          {mobileSelectionMode ? "Xong" : "Chọn"}
        </button>
      </div>

      <div className="product-modern-mobile-list">
        {mobileVisibleProducts.length ? (
          mobileVisibleProducts.map((item) => {
            const hasInactiveRecipeStock =
              isFoodServiceBusiness(business_type) &&
              getProductDetailInfo(
                item.product,
                business_type,
                make_products,
                data_stocks,
              ).hasInactiveRecipeStock;

            return (
              <MobileProductRow
                key={item.product.id}
                item={item}
                businessType={business_type}
                selected={selectedIds.includes(item.product.id)}
                selectionMode={mobileSelectionMode}
                templateLabel={intl.formatMessage({ id: "template.badge" })}
                canUpdate={canUpdate}
                canDelete={canDelete}
                onToggleProduct={onToggleProduct}
                onUpdateProduct={onUpdateProduct}
                onUpdateCost={onUpdateCost}
                onDeleteProduct={onDeleteProduct}
                onSoftHideProduct={onSoftHideProduct}
                onBestterProduct={onBestterProduct}
                hasInactiveRecipeStock={hasInactiveRecipeStock}
              />
            );
          })
        ) : (
          <div className="product-modern-mobile-empty">
            <i className="iconsminds-box-full" />
            {productDataLength === 0 ? (
              <>
                <strong>Chưa có sản phẩm nào</strong>
                <span>Cửa hàng của bạn chưa có sản phẩm. Bắt đầu thêm sản phẩm ngay!</span>
                {/* {canCreate && (
                  <Button
                    color="primary"
                    className="mt-3"
                    onClick={handleAddProduct}
                  >
                    <i className="simple-icon-plus mr-1" />
                    Thêm sản phẩm
                  </Button>
                )} */}
              </>
            ) : (
              <>
                <strong>Không tìm thấy sản phẩm</strong>
                <span>Hãy thử thay đổi từ khóa hoặc bộ lọc.</span>
              </>
            )}
          </div>
        )}
      </div>

      {mobileVisibleProducts.length < filteredProducts.length ? (
        <button
          type="button"
          className="product-modern-mobile-load-more"
          onClick={() => setMobileVisibleCount((count) => count + 20)}
        >
          Xem thêm
          <span>
            {AddCommaNumber(mobileVisibleProducts.length)}/{AddCommaNumber(filteredProducts.length)}
          </span>
        </button>
      ) : null}

      {mobileSelectionMode && selectedIds.length ? (
        <div className="product-modern-mobile-bulk-bar">
          <strong>{selectedIds.length} đã chọn</strong>
          <button type="button" disabled={!canUpdate} onClick={onBulkBestter}>
            <i className="simple-icon-star" />
            Bán chạy
          </button>
          <button type="button" disabled={!canUpdate} onClick={onBulkSoftHide}>
            <i className="simple-icon-lock" />
            Ẩn/hiện
          </button>
          <button
            type="button"
            className="is-danger"
            disabled={!canDelete}
            onClick={onBulkDelete}
          >
            <i className="simple-icon-trash" />
            Xóa
          </button>
        </div>
      ) : null}
    </div>

    <div className="d-none d-md-block">
    <Row>
      <Col xl="9">
        {hasTemplateProducts ? (
          <Row className="mb-3">
            <Col xs="12">
              <TemplateWarningCard onDismissed={onRefresh} />
            </Col>
          </Row>
        ) : null}

        {/* <div className="product-modern-main"> */}
          {/* LEGACY_PRODUCT_SUMMARY_OUTSIDE_CONTENT_LAYOUT_START
          The product KPI cards were rendered above product-modern-layout.
          They now live inside the 9-column main content area so the right aside
          starts from the top of the product content.
          LEGACY_PRODUCT_SUMMARY_OUTSIDE_CONTENT_LAYOUT_END */}
          {/* <div className="product-modern-summary-grid"> */}
          <Row className="mb-3">
            {statCards.map((card) => (
              <SummaryCard
                key={card.key}
                card={card}
                value={
                  card.key === "low_out"
                    ? `${AddCommaNumber(stats.low)} / ${AddCommaNumber(stats.out)}`
                    : stats[card.key]
                }
              />
            ))}
          </Row>
          {/* </div> */}

          <Card className="mb-3">
            <CardBody>
          {/* <div className="product-modern-toolbar"> */}
              <div className="mb-3">
            <div className="d-flex align-items-center flex-nowrap justify-content-end mb-2 tour-button-filter"
                  style={{ minWidth: "max-content" }}>
              <Input
                bsSize="sm"
                type="select"
                className="mr-2 flex-shrink-0"
                style={{
                  ...PRODUCT_TOOLBAR_SELECT_STYLE,
                  width: 170,
                  minWidth: 170,
                  maxWidth: 170,
                }}
                value={filters.category || ""}
                onChange={(e) => updateFilter("category", e.target.value)}
              >
                {/* <option value="">Danh mục: Tất cả</option> */}
                {(categoryOptions || []).map((name) => (
                  <option key={name.value} value={name.value}>
                    {name.label}
                  </option>
                ))}
              </Input>
              {/* <select
                className="product-modern-select"
                style={PRODUCT_TOOLBAR_SELECT_STYLE}
                value={filters.category}
                onChange={(event) => updateFilter("category", event.target.value)}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <Input
                bsSize="sm"
                type="select"
                className="mr-2 flex-shrink-0"
                style={{
                  ...PRODUCT_TOOLBAR_SELECT_STYLE,
                  width: 170,
                  minWidth: 170,
                  maxWidth: 170,
                }}
                value={filters.status || ""}
                onChange={(e) => updateFilter("status", e.target.value)}
              >
                {/* <option value="">Trạng thái: Tất cả</option> */}
                {(statusOptions || []).map((name) => (
                  <option key={name.value} value={name.value}>
                    {name.label}
                  </option>
                ))}
              </Input>
              {/* <select
                className="product-modern-select"
                style={PRODUCT_TOOLBAR_SELECT_STYLE}
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <Input
                bsSize="sm"
                type="select"
                className="mr-2 flex-shrink-0"
                style={{
                  ...PRODUCT_TOOLBAR_SELECT_STYLE,
                  width: 170,
                  minWidth: 170,
                  maxWidth: 170,
                }}
                value={filters.provider || ""}
                onChange={(e) => updateFilter("provider", e.target.value)}
              >
                {/* <option value="">Nhà cung cấp: Tất cả</option> */}
                {(providerOptions || []).map((name) => (
                  <option key={name.value} value={name.value}>
                    {name.label}
                  </option>
                ))}
              </Input>
              {/* <select
                className="product-modern-select"
                style={PRODUCT_TOOLBAR_SELECT_STYLE}
                value={filters.provider}
                onChange={(event) => updateFilter("provider", event.target.value)}
              >
                {providerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              <Input
                bsSize="sm"
                type="select"
                className="flex-shrink-0"
                style={{
                  ...PRODUCT_TOOLBAR_SELECT_STYLE,
                  width: 170,
                  minWidth: 170,
                  maxWidth: 170,
                }}
                value={filters.stock || ""}
                onChange={(e) => updateFilter("stock", e.target.value)}
              >
                {/* <option value="">Kho: Tất cả</option> */}
                {(stockOptions || []).map((name) => (
                  <option key={name.value} value={name.value}>
                    {name.label}
                  </option>
                ))}
              </Input>
              {/* <select
                className="product-modern-select product-modern-select-small"
                style={PRODUCT_TOOLBAR_SELECT_STYLE}
                value={filters.stock}
                onChange={(event) => updateFilter("stock", event.target.value)}
              >
                {stockOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              {activeFilterCount ? (
                <Button
                  size="sm"
                  color="outline-danger"
                  className="ml-2 product-modern-clear-filter-button flex-shrink-0"
                  onClick={resetFilters}
                  style={{
                    ...PRODUCT_TOOLBAR_DANGER_BUTTON_STYLE,
                    ...PRODUCT_TOOLBAR_CONTROL_HEIGHT_STYLE,
                  }}
                >
                  Bỏ lọc
                </Button>
              ) : null}
            </div>

            <div className="d-flex align-items-center flex-nowrap justify-content-end"
                    style={{ minWidth: "max-content" }}>
              {PRODUCT_SHOW_TABLE_VIEW ? (
                <div className="product-modern-view-toggle">
                  <Button
                    size="sm"
                    color={viewMode === "table" ? "primary" : "outline-primary"}
                    className="mr-2 flex-shrink-0 d-inline-flex align-items-center"
                    onClick={() => setViewMode("table")}
                  >
                    <i className="simple-icon-list mr-1" />
                    Bảng
                  </Button>
                  <Button
                    size="sm"
                    color={viewMode === "card" ? "primary" : "outline-primary"}
                    className="flex-shrink-0 d-inline-flex align-items-center"
                    onClick={() => setViewMode("card")}
                  >
                    <i className="simple-icon-grid mr-1" />
                    Thẻ
                  </Button>
                </div>
              ) : null}
              <div className="product-modern-action-buttons">
                <Button
                  size="sm"
                  color="link"
                  className="mr-2 flex-shrink-0"
                  onClick={handleExport}
                  style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                >
                  <i className="simple-icon-cloud-download mr-1" />
                  Xuất Excel
                </Button>
                <Button
                  size="sm"
                  color="link"
                  className="mr-2 flex-shrink-0"
                  onClick={() => setImportOpen(true)}
                  style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                >
                  <i className="simple-icon-cloud-upload mr-1" />
                  Nhập Excel
                </Button>
                <Button
                  size="sm"
                  color="link"
                  className="mr-2 flex-shrink-0"
                  onClick={() => setOcrOpen(true)}
                  style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                >
                  <i className="simple-icon-camera mr-1" />
                  Nhập từ ảnh (AI)
                </Button>
                {onAddFromTemplate && (
                  <Button
                    size="sm"
                    color="link"
                    className="flex-shrink-0 mr-2"
                    onClick={onAddFromTemplate}
                    style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                  >
                    {/* <i className="simple-icon-list mr-1" /> */}
                    Nhập từ mẫu
                  </Button>
                )}
                <Button
                  size="sm"
                  color="primary"
                  className="flex-shrink-0 tour-button-add"
                  disabled={!canCreate}
                  onClick={handleAddProduct}
                >
                  + Thêm mới
                </Button>
              </div>
            </div>
            </div>
          {/* </div> */}

          {selectedIds.length ? (
            <div className="product-modern-bulk-bar">
              <span>Đã chọn {selectedIds.length} sản phẩm</span>
              <div className="product-modern-bulk-actions">
                <Button
                  color="link"
                  size="sm"
                  className="mr-2 flex-shrink-0"
                  disabled={!canUpdate}
                  onClick={onBulkBestter}
                  style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                >
                  <i className="simple-icon-star mr-1" />
                  Bán chạy
                </Button>
                <Button
                  color="link"
                  size="sm"
                  className="mr-2 flex-shrink-0"
                  disabled={!canUpdate}
                  onClick={onBulkSoftHide}
                  style={PRODUCT_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                >
                  <i className="simple-icon-lock mr-1" />
                  Ẩn/hiện
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  className="flex-shrink-0"
                  disabled={!canDelete}
                  onClick={onBulkDelete}
                >
                  Xóa ({selectedIds.length})
                </Button>
              </div>
            </div>
          ) : null}

          {/* <div className="product-modern-content-card mb-5"> */}
            {PRODUCT_SHOW_TABLE_VIEW && viewMode === "table" ? (
              <div className="product-modern-table-wrap">
                <table className="product-modern-table">
                  <thead>
                    <tr>
                      <th className="product-modern-table-check">
                        <CustomInput
                          type="checkbox"
                          id="product-modern-check-all"
                          checked={isAllVisibleSelected}
                          onChange={() => onSelectAllProducts(visibleIds)}
                          label=""
                        />
                      </th>
                      <th>Sản phẩm</th>
                      <th>SKU / Barcode</th>
                      <th>Danh mục</th>
                      <th>Giá bán</th>
                      <th>Giá vốn</th>
                      <th>Tồn kho</th>
                      <th>Trạng thái</th>
                      <th>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProducts.length ? (
                      visibleProducts.map((item) => {
                        const hasInactiveRecipeStock =
                          isFoodServiceBusiness(business_type) &&
                          getProductDetailInfo(
                            item.product,
                            business_type,
                            make_products,
                            data_stocks,
                          ).hasInactiveRecipeStock;

                        return (
                          <ProductRow
                            key={item.product.id}
                            item={item}
                            businessType={business_type}
                            selected={selectedIds.includes(item.product.id)}
                            highlighted={String(highlightedProductId) === String(item.product.id)}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            onToggleProduct={onToggleProduct}
                            onUpdateProduct={onUpdateProduct}
                            onUpdateCost={onUpdateCost}
                            onDeleteProduct={onDeleteProduct}
                            onSoftHideProduct={onSoftHideProduct}
                            onBestterProduct={onBestterProduct}
                            expanded={expandedCardIds.includes(item.product.id)}
                            onToggleExpand={toggleCardExpand}
                            hasInactiveRecipeStock={hasInactiveRecipeStock}
                          />
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9">
                          <div className="product-modern-empty">
                            Chưa có sản phẩm phù hợp bộ lọc.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <>
                {/* LEGACY_PRODUCT_INLINE_CARD_LIST_START
                Product card list was rendered by ProductCard with product-modern-card-*
                wrappers here. It now uses ExpandableDataCardList so Product can share
                the same reusable card + checkbox + expand + action-menu shell as Stock.
                LEGACY_PRODUCT_INLINE_CARD_LIST_END */}
                <ExpandableDataCardList
                  data={visibleProducts}
                  columns={PRODUCT_CARD_COLUMNS}
                  gridTemplateColumns={productCardGridTemplate}
                  emptyMessage="Chưa có sản phẩm phù hợp bộ lọc."
                  selectable
                  titleHeaderLabel="Sản phẩm"
                  detailActionHeaderLabel="Chi tiết"
                  actionHeaderLabel="Khác"
                  allSelected={isAllVisibleSelected}
                  onToggleSelectAll={() => onSelectAllProducts(visibleIds)}
                  selectedIds={selectedIds}
                  onToggleItem={(productId) => onToggleProduct(productId)}
                  expandedIds={expandedCardIds}
                  onToggleExpand={toggleCardExpand}
                  highlightedId={highlightedProductId}
                  getItemId={(item) => item.product.id}
                  getItemHasDetails={(item) =>
                    getProductDetailInfo(
                      item.product,
                      business_type,
                      make_products,
                      data_stocks,
                    ).hasDetails
                  }
                  getVisualStyle={(item) =>
                    PRODUCT_CARD_VISUAL_STYLE[item.status.key] ||
                    PRODUCT_CARD_VISUAL_STYLE.active
                  }
                  renderTitle={({ item, visualStyle, isHighlighted }) => {
                    const product = item.product;
                    const isCardHighlighted = isHighlighted || String(highlightedProductId) === String(product.id);
                    const hasInactiveRecipeStock =
                      isFoodServiceBusiness(business_type) &&
                      getProductDetailInfo(
                        product,
                        business_type,
                        make_products,
                        data_stocks,
                      ).hasInactiveRecipeStock;

                    return (
                      <div className="product-modern-card-product">
                        <ProductThumb
                          product={product}
                          businessType={business_type}
                        />
                        <div className="min-width-zero" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                          <div
                            className="product-modern-card-name"
                            title={product.name}
                            style={{
                              color: isCardHighlighted ? "#1d4ed8" : visualStyle.titleColor,
                              fontWeight: isCardHighlighted ? 800 : undefined,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: 1.35,
                              maxHeight: "2.7em",
                              maxWidth: "100%",
                            }}
                          >
                            {product.name}
                          </div>
                          {hasInactiveRecipeStock ? (
                            <div
                              className="product-modern-product-badge"
                              style={{
                                color: "#d97706",
                                backgroundColor: "#fef3c7",
                                border: "1px solid #fde68a",
                                padding: "1px 6px",
                                borderRadius: 4,
                                fontWeight: 600,
                                fontSize: "11px",
                                display: "inline-flex",
                                alignItems: "center",
                                marginTop: "2px",
                              }}
                              title="Định lượng có nguyên liệu đã ngừng kinh doanh"
                            >
                              <i className="simple-icon-exclamation mr-1" />
                              Định lượng có nguyên liệu ngừng KD
                            </div>
                          ) : null}
                          {isCardHighlighted ? (
                            <div className="product-modern-product-badge" style={{ color: "#2563eb", fontWeight: 800 }}>
                              <i className="simple-icon-fire mr-1" />
                              Đang chọn từ Bán chạy
                            </div>
                          ) : null}
                          {product.isTemplate ? (
                            <div className="product-modern-product-badge">
                              <i className="simple-icon-layers mr-1" />
                              {intl.formatMessage({ id: "template.badge" })}
                            </div>
                          ) : null}
                          {product.bestter && !isCardHighlighted ? (
                            <div className="product-modern-product-badge">
                              <i className="simple-icon-star mr-1" />
                              Bán chạy
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  }}
                  renderMainValue={({ item, column }) =>
                    renderProductCardColumnValue(item, column.key)
                  }
                  renderExpandedContent={({ item }) => (
                    <ProductDetailPanel
                      product={item.product}
                      businessType={business_type}
                      makeProducts={make_products}
                      dataStocks={data_stocks}
                      statusKey={item.status.key}
                    />
                  )}
                  renderDetailToggle={({ item, isExpanded, hasDetails, toggleExpand }) => {
                    const detailInfo = getProductDetailInfo(
                      item.product,
                      business_type,
                      make_products,
                      data_stocks,
                    );
                    return (
                      hasDetails ? (
                        <VariantExpandButton
                          expanded={isExpanded}
                          count={detailInfo.detailCount}
                          label={detailInfo.buttonLabel}
                          onClick={toggleExpand}
                        />
                      ) : null
                    );
                  }}
                  renderActions={({ item }) => (
                    <div className="product-modern-card-actions">
                        <ProductActions
                          item={item}
                          canUpdate={canUpdate}
                          canDelete={canDelete}
                          onUpdateProduct={onUpdateProduct}
                          onUpdateCost={onUpdateCost}
                          onDeleteProduct={onDeleteProduct}
                          onSoftHideProduct={onSoftHideProduct}
                          onBestterProduct={onBestterProduct}
                        />
                      </div>
                  )}
                />
              </>
            )}

            <div className="product-modern-pagination">
              <div>
                Hiển thị {displayStart} - {displayEnd} của{" "}
                {AddCommaNumber(filteredProducts.length)} sản phẩm
              </div>
              <div className="product-modern-pagination-controls">
                <UncontrolledDropdown className="d-inline-block mr-3">
                  <DropdownToggle caret color="outline-dark" size="xs">
                    {pageSize}
                  </DropdownToggle>
                  <DropdownMenu right>
                    {[12, 24, 48].map((size) => (
                      <DropdownItem
                        key={size}
                        onClick={() => setPageSize(size)}
                      >
                        {size}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPage={totalPage}
                  onChangePage={handleChangePage}
                />
              </div>
            </div>
          {/* </div> */}
          </CardBody></Card>
        {/* </div> */}
      </Col>
      <Col xl="3">
        <aside className="product-modern-sidebar mb-5">
          <div className="product-modern-side-card">
            <div className="product-modern-side-title">
              <span>Thống kê theo danh mục</span>
              {filters.category && filters.category !== "all" ? (
                <button
                  type="button"
                  className="product-modern-side-reset-btn"
                  onClick={() => updateFilter("category", "all")}
                  title="Bỏ lọc danh mục"
                >
                  <i className="simple-icon-close mr-1" />
                  Bỏ lọc
                </button>
              ) : (
                <button type="button" onClick={onOpenCategories}>
                  Xem tất cả
                </button>
              )}
            </div>
            {categoryStats.map((category) => {
              const isCategoryActive = filters.category === category.name;
              return (
                <button
                  type="button"
                  key={category.name}
                  className={classnames("product-modern-category-row", {
                    active: isCategoryActive,
                  })}
                  onClick={() =>
                    updateFilter("category", isCategoryActive ? "all" : category.name)
                  }
                  title={
                    isCategoryActive
                      ? "Bấm để bỏ lọc danh mục này"
                      : `Lọc theo ${category.name}`
                  }
                >
                  <span
                    className={`product-modern-category-icon product-modern-tone-${category.tone}`}
                  >
                    <i
                      className={
                        isCategoryActive
                          ? "simple-icon-check font-weight-bold"
                          : "iconsminds-folder"
                      }
                    />
                  </span>
                  <span className="product-modern-category-name">{category.name}</span>
                  <strong>{AddCommaNumber(category.count)}</strong>
                </button>
              );
            })}
          </div>

          <div className="product-modern-side-card">
            <div className="product-modern-side-title">
              <span>Sản phẩm bán chạy</span>
              <div className="d-flex align-items-center">
                {highlightedProductId ? (
                  <button
                    type="button"
                    className="mr-2 product-modern-side-reset-btn"
                    onClick={() => setHighlightedProductId(null)}
                    title="Bỏ chọn sản phẩm"
                  >
                    <i className="simple-icon-close mr-1" />
                    Bỏ chọn
                  </button>
                ) : null}
                <UncontrolledDropdown className="product-modern-best-range-dropdown">
                  <DropdownToggle caret color="link" size="sm">
                    {
                      BEST_SELLER_RANGE_OPTIONS.find(
                        (option) => option.value === bestSellerRange,
                      )?.label
                    }
                  </DropdownToggle>
                  <DropdownMenu right>
                    {BEST_SELLER_RANGE_OPTIONS.map((option) => (
                      <DropdownItem
                        key={option.value}
                        active={option.value === bestSellerRange}
                        onClick={() => setBestSellerRange(option.value)}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
            {bestSellers.map((item, index) => {
              const isItemActive =
                String(highlightedProductId) === String(item.product.id);
              return (
                <button
                  type="button"
                  key={item.product.id}
                  className={classnames("product-modern-best-row", {
                    active: isItemActive,
                  })}
                  onClick={() => handleBestSellerClick(item.product)}
                  title={
                    isItemActive
                      ? "Bấm để bỏ chọn"
                      : "Bấm để xem và highlight sản phẩm trong danh sách"
                  }
                >
                  <span className={`product-modern-rank rank-${index + 1}`}>
                    {isItemActive ? <i className="simple-icon-check" /> : index + 1}
                  </span>
                  <span className="product-modern-best-thumb-wrap">
                    <ProductThumb product={item.product} businessType={business_type} />
                  </span>
                  <span className="min-width-zero">
                    <span className="product-modern-best-title-row">
                      <span className="product-modern-best-name">
                        {item.product.name}
                      </span>
                      {item.product.isTemplate ? (
                        <span className="badge badge-info ml-1">
                          {intl.formatMessage({ id: "template.badge" })}
                        </span>
                      ) : null}
                    </span>
                    <span className="product-modern-secondary-text">
                      {isItemActive ? (
                        <span className="text-primary font-weight-bold">
                          <i className="simple-icon-fire mr-1" />
                          Đang xem
                        </span>
                      ) : item.product.bestter ? (
                        "Đang nổi bật"
                      ) : (
                        "Sẵn sàng bán"
                      )}
                    </span>
                  </span>
                  <strong className="product-modern-best-sold-count">
                    {AddCommaNumber(item.bestSellerSoldCount || 0)}
                  </strong>
                </button>
              );
            })}
          </div>

          <div className="product-modern-side-card">
            <div className="product-modern-side-title">
              <span>Thao tác nhanh</span>
            </div>
            <div className="product-modern-quick-grid">
              <button type="button" onClick={handleAddProduct} disabled={!canCreate}>
                <i className="simple-icon-plus" />
                <strong>Thêm sản phẩm</strong>
                <span>Tạo mới sản phẩm</span>
              </button>
              {onAddFromTemplate && (
                <button type="button" onClick={onAddFromTemplate}>
                  <i className="simple-icon-list" />
                  <strong>Nhập từ mẫu</strong>
                  <span>Hàng mẫu tạp hóa, FMCG</span>
                </button>
              )}
              <button type="button" onClick={() => setImportOpen(true)}>
                <i className="simple-icon-cloud-upload" />
                <strong>Nhập sản phẩm</strong>
                <span>Nhập từ file Excel</span>
              </button>
              <button type="button" onClick={() => setOcrOpen(true)}>
                <i className="simple-icon-camera" />
                <strong>Nhập từ ảnh (AI)</strong>
                <span>Trích xuất bằng AI</span>
              </button>
              <button type="button" onClick={onAddCategory}>
                <i className="simple-icon-plus" />
                <strong>Tạo danh mục</strong>
                <span>Thêm danh mục mới</span>
              </button>
              <button type="button" onClick={onOpenCategories}>
                <i className="simple-icon-folder-alt" />
                <strong>Quản lý danh mục</strong>
                <span>Thêm, sửa danh mục</span>
              </button>
            </div>
          </div>
        </aside>
      </Col>

      <Modal
        isOpen={importOpen}
        toggle={() => setImportOpen(false)}
        className={classnames(
          "table-modal cursor-pointer product-import-modal",
        )}
        modalClassName="table-modal-dialog-wrapper product-mobile-right-drawer-modal"
        wrapClassName="product-mobile-right-drawer-wrap"
        contentClassName="table-modal-content-wrapper"
      >
        <ModalHeader toggle={() => setImportOpen(false)}>
          Nhập sản phẩm từ Excel
        </ModalHeader>
        <ModalBody>
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div className="text-muted small">
              Hỗ trợ file export mới nhiều sheet và template Excel cũ.
            </div>
            <div className="d-flex align-items-center">
              <Button
                type="button"
                color="secondary"
                outline
                size="sm"
                className="mr-2"
                onClick={handleDownloadImportTemplate}
              >
                <i className="simple-icon-cloud-download mr-1" />
                Tải file mẫu
              </Button>
              <Input
                type="file"
                accept=".xlsx,.xls"
                bsSize="sm"
                onChange={handleImportFileUpload}
                style={{ maxWidth: 280 }}
              />
            </div>
          </div>
          <ModalAddProducts
            handleImportProduct={onImportProduct}
            data_all={data_all}
            categories={data_categories}
            data_providers={data_providers}
            business_type={business_type}
            savedData={savedImportData}
            onDataChange={handleImportDataChange}
          />
        </ModalBody>
      </Modal>
      <Modal
        isOpen={ocrOpen}
        toggle={() => setOcrOpen(false)}
        className={classnames(
          "table-modal cursor-pointer product-import-modal",
        )}
        modalClassName="table-modal-dialog-wrapper product-mobile-right-drawer-modal"
        wrapClassName="product-mobile-right-drawer-wrap"
        contentClassName="table-modal-content-wrapper"
      >
        <ModalHeader toggle={() => setOcrOpen(false)}>
          Nhập sản phẩm từ hình ảnh (AI OCR)
        </ModalHeader>
        <ModalBody>
          <ModalAddProductsFromImage
            businessType={business_type}
            onSaveSuccess={() => {
              setOcrOpen(false);
              if (onRefresh) onRefresh();
            }}
          />
        </ModalBody>
      </Modal>
    </Row>
    </div>

    <Modal
      isOpen={mobileFilterOpen}
      toggle={() => setMobileFilterOpen(false)}
      className="product-modern-mobile-filter-modal"
      modalClassName="product-mobile-right-drawer-modal"
      wrapClassName="product-mobile-right-drawer-wrap"
    >
      <ModalHeader toggle={() => setMobileFilterOpen(false)}>
        Bộ lọc sản phẩm
      </ModalHeader>
      <ModalBody>
        <label htmlFor="product-mobile-category">Danh mục</label>
        <Input
          id="product-mobile-category"
          type="select"
          value={filters.category}
          onChange={(event) => updateFilter("category", event.target.value)}
        >
          {categoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>

        <label htmlFor="product-mobile-provider">Nhà cung cấp</label>
        <Input
          id="product-mobile-provider"
          type="select"
          value={filters.provider}
          onChange={(event) => updateFilter("provider", event.target.value)}
        >
          {providerOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>

        <label htmlFor="product-mobile-stock">Trạng thái kho</label>
        <Input
          id="product-mobile-stock"
          type="select"
          value={filters.stock}
          onChange={(event) => updateFilter("stock", event.target.value)}
        >
          {stockOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>

        <div className="product-modern-mobile-filter-actions">
          <Button color="light" onClick={resetFilters}>
            Bỏ lọc
          </Button>
          <Button color="primary" onClick={() => setMobileFilterOpen(false)}>
            Áp dụng
          </Button>
        </div>
      </ModalBody>
    </Modal>
    </>
  );
};

export {
  appendWorkbookSheet,
  buildLegacyWorkbookImportData,
  buildModernWorkbookImportData,
  enrichProducts,
  getCostInfo,
  getInventoryInfo,
  getLowStockThreshold,
  getOptionsExportRows,
  getPriceInfo,
  getProductExportRows,
  getProviderNames,
  getRecipeExportRows,
  getRecipeUnitCost,
  getStatusMeta,
  getVariantEffectivePrice,
  getVariantExportRows,
  getVariantPriceText,
  normalizeImportDate,
  parseVariantAttributes,
};

export default injectIntl(ProductModernDashboard);
